import { DetailCard, DefinitionList, Flexbox, Input, CheckboxRow, Text, CurrencyInput, useFuzzy, Modal, ModalBody, ModalHeader, Padding, SingleObjectSelector, Spacing, ModalFooter, Button, MultiObjectSelector, IconButton, colors, Box, Tooltip, BasicTooltip, IconCircleInformation, Stack, StackDirectionType, ActionModal, TextLink, ExpandableModule, CircleSpinnerInline, Field, AdvancedSelectInput, MultiObjectSelect, DatePickerInput2, MessageBox, IconPencil, IconRemove, RadioButtonGroup, TextareaAutosize, CircleSpinner, ExpandableRow, ExpandableHead, ExpandableBody, StrippedRows, Columns } from '@newfront-insurance/core-ui';
import { PolicyStatus, DslCustomInputType, claimsMadeOrOccurrenceOptions, coverageTerritoryTypeOptions, auditableTypeOptions, terrorismRiskInsuranceActTypeOptions } from '@newfront-insurance/coverage-api';
import { addCustomSchemaHandler, getStringifiedFieldValue, SchemaDisplay, FormikSchemaInput } from '@newfront-insurance/dsl-schema-ui';
import React, { useState, Suspense, useMemo, useCallback, useEffect } from 'react';
import { moneyToString, toNumberFromMoney, toMoney } from '@newfront-insurance/core-money';
import { useNotifications } from '@newfront-insurance/next-notifications';
import { formatDollars, DateFormatStyle, formatDate } from '@newfront-insurance/string-formatters';
import { useToggle } from 'react-use';
import styled from 'styled-components';
import { u as useAdminTRPC } from './trpc-2b4d7b2f.esm.js';
import { Formik, useFormikContext, FieldArray } from 'formik';
import { toRecord, isNil, isNotNil } from '@newfront-insurance/core';
import get from 'lodash/get';
import { L as LineOfCoverageStatus } from './line-of-coverage-851873c4.esm.js';
import '@babel/runtime/helpers/extends';
import pluralize from 'pluralize';
import * as yup from 'yup';
import { DSLSpreadsheet } from '@newfront-insurance/spreadsheet';
import { createProvider, useProvider } from '@newfront-insurance/react-provision';

const LINE_OF_COVERAGE_STATUS_LABELS = {
  [LineOfCoverageStatus.ACTIVE]: {
    text: 'Active',
    type: 'success'
  },
  [LineOfCoverageStatus.UPCOMING]: {
    text: 'Upcoming',
    type: 'info'
  },
  [LineOfCoverageStatus.EXPIRED]: {
    text: 'Expired',
    type: 'secondary'
  }
};
function LineOfCoverageCard(_ref) {
  var _policy$billing, _policy$carrier;

  let {
    lineOfCoverage,
    actions,
    cardProps,
    hoverProps,
    isExpandable = false,
    defaultExpanded = true,
    selectableOptions,
    isHovering,
    isHighlighted,
    eyebrowProps,
    policyDetailsLink
  } = _ref;
  const {
    policy,
    startDate,
    endDate
  } = lineOfCoverage;
  return /*#__PURE__*/React.createElement(DetailCard, {
    title: lineOfCoverage.title,
    badgeProps: lineOfCoverage.statusLabel ? LINE_OF_COVERAGE_STATUS_LABELS[lineOfCoverage.statusLabel] : undefined,
    actions: actions,
    cardProps: cardProps,
    hoverProps: hoverProps,
    isExpandable: isExpandable,
    defaultExpanded: defaultExpanded,
    selectableOptions: selectableOptions,
    isHovering: isHovering,
    isHighlighted: isHighlighted,
    eyebrowProps: eyebrowProps
  }, /*#__PURE__*/React.createElement(DefinitionList, {
    items: [{
      title: 'Policy number',
      type: 'number',
      value: (policy === null || policy === void 0 ? void 0 : policy.policyNumber) ?? '-',
      href: policyDetailsLink === null || policyDetailsLink === void 0 ? void 0 : policyDetailsLink.href,
      onClick: policyDetailsLink === null || policyDetailsLink === void 0 ? void 0 : policyDetailsLink.onClick,
      tooltipText: policyDetailsLink ? 'View policy details' : undefined
    }, {
      title: 'Premium',
      type: 'currency',
      value: (policy === null || policy === void 0 ? void 0 : (_policy$billing = policy.billing) === null || _policy$billing === void 0 ? void 0 : _policy$billing.premium) !== undefined ? formatDollars(policy.billing.premium) : '-'
    }, {
      title: 'Effective dates',
      type: 'dateRange',
      value: `${startDate ? startDate.format({
        style: DateFormatStyle.STANDARD
      }) : 'N/A'} - ${endDate ? endDate.format({
        style: DateFormatStyle.STANDARD
      }) : 'N/A'}`
    }, {
      title: 'Carrier',
      type: 'business',
      value: (policy === null || policy === void 0 ? void 0 : (_policy$carrier = policy.carrier) === null || _policy$carrier === void 0 ? void 0 : _policy$carrier.name) ?? '-'
    }]
  }));
}

const policyStatusBadgePropsMap = {
  [PolicyStatus.ACTIVE]: {
    text: 'Active',
    type: 'success'
  },
  [PolicyStatus.UPCOMING]: {
    text: 'Upcoming',
    type: 'info'
  },
  [PolicyStatus.EXPIRED]: {
    text: 'Expired',
    type: 'secondary'
  },
  [PolicyStatus.CANCELLED]: {
    text: 'Cancelled',
    type: 'secondary'
  }
};
function PolicyCard(_ref) {
  var _policy$lineOfCoverag, _marketAccess$carrier;

  let {
    policy,
    actions,
    cardProps,
    hoverProps,
    isExpandable = false,
    defaultExpanded = true,
    selectableOptions,
    isHovering,
    isHighlighted,
    eyebrowProps
  } = _ref;
  const {
    definition,
    policyNumber,
    effectiveDate,
    expirationDate,
    details,
    marketAccess
  } = policy;
  return /*#__PURE__*/React.createElement(DetailCard, {
    title: `${definition === null || definition === void 0 ? void 0 : definition.readableName} - #${policyNumber}`,
    subtitle: `${effectiveDate ? effectiveDate.format({
      style: DateFormatStyle.STANDARD
    }) : 'N/A'} - ${expirationDate ? expirationDate.format({
      style: DateFormatStyle.STANDARD
    }) : 'N/A'}`,
    badgeProps: policyStatusBadgePropsMap[policy.status] ?? undefined,
    actions: actions,
    cardProps: cardProps,
    hoverProps: hoverProps,
    isExpandable: isExpandable,
    defaultExpanded: defaultExpanded,
    selectableOptions: selectableOptions,
    isHovering: isHovering,
    isHighlighted: isHighlighted,
    eyebrowProps: eyebrowProps
  }, /*#__PURE__*/React.createElement(DefinitionList, {
    items: [{
      title: 'Policy display name',
      type: 'contact',
      value: (details === null || details === void 0 ? void 0 : details.displayName) ?? '-'
    }, {
      title: 'Lines of coverage',
      type: 'document',
      value: ((_policy$lineOfCoverag = policy.lineOfCoverageNames) === null || _policy$lineOfCoverag === void 0 ? void 0 : _policy$lineOfCoverag.join(', ')) ?? '-'
    }, {
      title: 'Carrier',
      type: 'business',
      value: ((_marketAccess$carrier = marketAccess.carrier) === null || _marketAccess$carrier === void 0 ? void 0 : _marketAccess$carrier.name) ?? '-'
    }]
  }));
}

function NumberOrCheckboxDslInput(_ref) {
  let {
    onChange,
    value,
    metadata
  } = _ref;

  // we can assume the metadata is always defined
  if (!metadata) {
    return null;
  }

  const checkboxValue = (value === null || value === void 0 ? void 0 : value[metadata.checkboxFieldName]) || false;

  function handleNumberChange(stringValue) {
    // metadata should always be defined
    if (!metadata) {
      return;
    }

    if (!stringValue && !checkboxValue) {
      onChange(undefined);
      return;
    }

    onChange({ ...value,
      [metadata.numberFieldName]: parseInt(stringValue, 10),
      [metadata.checkboxFieldName]: checkboxValue || false
    });
  }

  function handleCheckboxChange(isActive) {
    // metadata should always be defined
    if (!metadata) {
      return;
    }

    if (!isActive) {
      onChange(undefined);
      return;
    }

    onChange({
      [metadata.checkboxFieldName]: isActive
    });
  }

  return /*#__PURE__*/React.createElement(Flexbox, {
    flexDirection: "column",
    gap: 8,
    marginTop: "auto"
  }, /*#__PURE__*/React.createElement(Input, {
    type: "number",
    appendText: metadata === null || metadata === void 0 ? void 0 : metadata.appendText,
    value: (value === null || value === void 0 ? void 0 : value[metadata.numberFieldName]) ?? '',
    onChange: handleNumberChange,
    disabled: !!checkboxValue
  }), /*#__PURE__*/React.createElement(CheckboxRow, {
    label: (metadata === null || metadata === void 0 ? void 0 : metadata.checkboxFieldLabel) ?? '',
    isActive: !!checkboxValue,
    onClick: handleCheckboxChange
  }));
}
const NumberOrCheckboxDisplay = _ref2 => {
  let {
    field,
    value
  } = _ref2;
  return /*#__PURE__*/React.createElement(Text, null, getStringifiedFieldValue(value, field));
};
addCustomSchemaHandler(DslCustomInputType.NUMBER_OR_CHECKBOX, NumberOrCheckboxDisplay, NumberOrCheckboxDslInput, (value, field) => {
  if (!value || !field) {
    return '-';
  }

  if (value !== null && value !== void 0 && value[field.inputMetadata.checkboxFieldName]) {
    return field.inputMetadata.checkboxFieldLabel;
  }

  return `${value === null || value === void 0 ? void 0 : value[field.inputMetadata.numberFieldName]}${field.inputMetadata.appendText ?? ''}`;
});

function CurrencyOrCheckboxDslInput(_ref) {
  let {
    onChange,
    value,
    metadata
  } = _ref;

  // we can assume the metadata is always defined
  if (!metadata) {
    return null;
  }

  const checkboxValue = (value === null || value === void 0 ? void 0 : value[metadata.checkboxFieldName]) || false;
  const moneyValue = value === null || value === void 0 ? void 0 : value[metadata.currencyFieldName];

  function handleCurrencyChange(currencyValue) {
    // metadata should always be defined
    if (!metadata) {
      return;
    }

    if (checkboxValue) {
      return;
    }

    if (!currencyValue && !checkboxValue) {
      onChange(undefined);
      return;
    }

    onChange({ ...value,
      [metadata.currencyFieldName]: toMoney(currencyValue),
      [metadata.checkboxFieldName]: checkboxValue || false
    });
  }

  function handleCheckboxChange(isActive) {
    // metadata should always be defined
    if (!metadata) {
      return;
    }

    if (!isActive) {
      onChange(undefined);
      return;
    }

    onChange({
      [metadata.checkboxFieldName]: true
    });
  }

  return /*#__PURE__*/React.createElement(Flexbox, {
    flexDirection: "column",
    gap: 8,
    marginTop: "auto"
  }, /*#__PURE__*/React.createElement(CurrencyInput, {
    onChange: handleCurrencyChange,
    value: moneyValue ? toNumberFromMoney(moneyValue) : '',
    disabled: !!checkboxValue,
    name: metadata.currencyFieldName
  }), /*#__PURE__*/React.createElement(CheckboxRow, {
    label: (metadata === null || metadata === void 0 ? void 0 : metadata.checkboxFieldLabel) ?? '',
    isActive: !!checkboxValue,
    onClick: handleCheckboxChange
  }));
}
const CurrencyOrCheckboxDisplay = _ref2 => {
  let {
    field,
    value
  } = _ref2;
  return /*#__PURE__*/React.createElement(Text, null, getStringifiedFieldValue(value, field));
};
addCustomSchemaHandler(DslCustomInputType.CURRENCY_OR_CHECKBOX, CurrencyOrCheckboxDisplay, CurrencyOrCheckboxDslInput, (value, field) => {
  if (!value || !field) {
    return '-';
  }

  if (value !== null && value !== void 0 && value[field.inputMetadata.checkboxFieldName]) {
    return field.inputMetadata.checkboxFieldLabel;
  }

  const moneyValue = value === null || value === void 0 ? void 0 : value[field.inputMetadata.currencyFieldName];
  return moneyValue ? moneyToString(moneyValue) : '-';
});

function useLineOfCoverageMetadata(params, options) {
  const {
    useQuery
  } = useAdminTRPC();
  return useQuery(['loc.getLocMetadata', params], options);
}

function LineOfCoverageDataDisplay(_ref) {
  var _claimsMadeOrOccurren, _coverageTerritoryTyp, _auditableTypeOptions, _terrorismRiskInsuran;

  let {
    lineOfCoverage,
    onlyDslData
  } = _ref;
  const {
    displayName,
    startDate,
    endDate,
    type: lineOfCoverageType,
    version: lineOfCoverageVersion,
    data: lineOfCoverageData,
    globalFields
  } = lineOfCoverage;
  const {
    data
  } = useLineOfCoverageMetadata({
    lineOfCoverageType,
    lineOfCoverageVersion
  }, {
    suspense: true
  });
  const claimsMadeOrOccurrenceValueToLabel = (_claimsMadeOrOccurren = claimsMadeOrOccurrenceOptions.find(option => option.value === (globalFields === null || globalFields === void 0 ? void 0 : globalFields.claimsMadeOrOccurrence))) === null || _claimsMadeOrOccurren === void 0 ? void 0 : _claimsMadeOrOccurren.label;
  const coverageTerritoryValueToLabel = (_coverageTerritoryTyp = coverageTerritoryTypeOptions.find(option => option.value === (globalFields === null || globalFields === void 0 ? void 0 : globalFields.coverageTerritory))) === null || _coverageTerritoryTyp === void 0 ? void 0 : _coverageTerritoryTyp.label;
  const auditableValueToLabel = (_auditableTypeOptions = auditableTypeOptions.find(option => option.value === (globalFields === null || globalFields === void 0 ? void 0 : globalFields.auditable))) === null || _auditableTypeOptions === void 0 ? void 0 : _auditableTypeOptions.label;
  const terrorismRiskInsuranceActValueToLabel = (_terrorismRiskInsuran = terrorismRiskInsuranceActTypeOptions.find(option => option.value === (globalFields === null || globalFields === void 0 ? void 0 : globalFields.terrorismRiskInsuranceAct))) === null || _terrorismRiskInsuran === void 0 ? void 0 : _terrorismRiskInsuran.label;
  return /*#__PURE__*/React.createElement(Flexbox, {
    gap: 32,
    flexDirection: "column"
  }, !onlyDslData && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Flexbox, {
    flexDirection: "column"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "small",
    color: "light"
  }, "Display name"), /*#__PURE__*/React.createElement(Text, {
    size: "regular",
    color: "dark",
    weight: 300
  }, displayName || '-')), /*#__PURE__*/React.createElement(Flexbox, {
    gap: 16,
    flexDirection: "row"
  }, /*#__PURE__*/React.createElement(Flexbox, {
    flexDirection: "column"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "small",
    color: "light"
  }, "Start date"), /*#__PURE__*/React.createElement(Text, {
    size: "regular",
    color: "dark",
    weight: 300
  }, startDate ? startDate.format({
    style: DateFormatStyle.STANDARD
  }) : '')), /*#__PURE__*/React.createElement(Flexbox, {
    flexDirection: "column"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "small",
    color: "light"
  }, "End date"), /*#__PURE__*/React.createElement(Text, {
    size: "regular",
    color: "dark",
    weight: 300
  }, endDate ? endDate.format({
    style: DateFormatStyle.STANDARD
  }) : '')))), /*#__PURE__*/React.createElement(Flexbox, {
    flexDirection: "column"
  }, claimsMadeOrOccurrenceValueToLabel && /*#__PURE__*/React.createElement(Flexbox, {
    flexDirection: "column"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "regular",
    color: "dark",
    weight: 300
  }, claimsMadeOrOccurrenceValueToLabel)), /*#__PURE__*/React.createElement(Text, {
    size: "small",
    color: "light"
  }, "Coverage Territory"), /*#__PURE__*/React.createElement(Text, {
    size: "regular",
    color: "dark",
    weight: 300
  }, coverageTerritoryValueToLabel || '-')), /*#__PURE__*/React.createElement(Flexbox, {
    flexDirection: "column"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "small",
    color: "light"
  }, "Auditable?"), /*#__PURE__*/React.createElement(Text, {
    size: "regular",
    color: "dark",
    weight: 300
  }, auditableValueToLabel || '-')), /*#__PURE__*/React.createElement(SchemaDisplay, {
    name: lineOfCoverageType,
    schema: data === null || data === void 0 ? void 0 : data.schema,
    values: lineOfCoverageData
  }), /*#__PURE__*/React.createElement(Flexbox, {
    flexDirection: "column"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "small",
    color: "light"
  }, "Terrorism Risk Insurance Act (TRIA)"), /*#__PURE__*/React.createElement(Text, {
    size: "regular",
    color: "dark",
    weight: 300
  }, terrorismRiskInsuranceActValueToLabel || '-')), /*#__PURE__*/React.createElement(Flexbox, {
    flexDirection: "column"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "small",
    color: "light"
  }, "Additional Details"), /*#__PURE__*/React.createElement(Text, {
    prewrap: true,
    size: "regular",
    color: "dark",
    weight: 300
  }, (globalFields === null || globalFields === void 0 ? void 0 : globalFields.additionalDetails) || '-')));
}

function useLineOfCoverageLineageCandidates(params, options) {
  const {
    useQuery
  } = useAdminTRPC();
  return useQuery(['loc.getLocLineageCandidates', params], options);
}

function useLineageCandidatesLocTypes(params, options) {
  const {
    useQuery
  } = useAdminTRPC();
  return useQuery(['loc.getLineageCandidatesLocTypes', params], options);
}

function LineOfCoverageLineageAddModal(_ref) {
  let {
    lineageType,
    lineOfCoverageDefinition,
    lineOfCoverageUuid,
    accountUuid,
    isOpen,
    currLocs,
    isSubmitting,
    toggleIsOpen,
    onAddClick
  } = _ref;
  const localLocUuids = currLocs.map(loc => loc.lineOfCoverage.uuid ?? '');
  const {
    data: lineOfCoverageDefinitions,
    isLoading
  } = useLineageCandidatesLocTypes({
    lineOfCoverageUuid,
    localLocUuids,
    candidateType: lineageType ?? 'next'
  });
  const {
    results,
    search
  } = useFuzzy(lineOfCoverageDefinitions ?? [], {
    keys: ['readableName']
  });
  const [selectedType, setSelectedType] = useState(lineOfCoverageDefinition);
  const [selectedLinesOfCoverage, setSelectedLinesOfCoverage] = useState([]);
  return /*#__PURE__*/React.createElement(Modal, {
    isOpen: isOpen,
    onToggle: toggleIsOpen
  }, /*#__PURE__*/React.createElement(ModalBody, null, /*#__PURE__*/React.createElement(ModalHeader, {
    titleText: `Link ${lineageType} line of coverage`,
    onClose: toggleIsOpen
  }), /*#__PURE__*/React.createElement(Padding, {
    size: 40
  }, /*#__PURE__*/React.createElement(Flexbox, {
    flexDirection: "column"
  }, /*#__PURE__*/React.createElement(Text, {
    weight: 600,
    style: {
      paddingBottom: '6px'
    }
  }, "LOC type"), /*#__PURE__*/React.createElement(SingleObjectSelector, {
    items: results,
    itemToString: def => (def === null || def === void 0 ? void 0 : def.readableName) ?? '',
    getItemKey: def => def === null || def === void 0 ? void 0 : def.id,
    onSelect: def => setSelectedType(def),
    renderItem: (def, _ref2) => {
      let {
        isHovering,
        isSelected
      } = _ref2;
      return /*#__PURE__*/React.createElement(DetailCard, {
        title: def === null || def === void 0 ? void 0 : def.readableName,
        isHighlighted: isSelected,
        isHovering: isHovering,
        cardProps: {
          hideBorder: true,
          hasRadius: false
        }
      });
    },
    selectedItem: selectedType,
    onSearch: search,
    isLoading: isLoading
  }), /*#__PURE__*/React.createElement(Spacing, {
    height: 24
  }), /*#__PURE__*/React.createElement(Text, {
    weight: 600,
    style: {
      paddingBottom: '6px'
    }
  }, "Line of coverage"), /*#__PURE__*/React.createElement(LineOfCoverageSelector, {
    selectedType: selectedType,
    lineageType: lineageType,
    lineOfCoverageUuid: lineOfCoverageUuid,
    accountUuid: accountUuid,
    selectedLinesOfCoverage: selectedLinesOfCoverage,
    setSelectedLinesOfCoverage: setSelectedLinesOfCoverage,
    currLocs: localLocUuids
  }))), /*#__PURE__*/React.createElement(ModalFooter, null, /*#__PURE__*/React.createElement(Button, {
    size: "secondary",
    onClick: () => {
      toggleIsOpen();
    }
  }, "Cancel"), /*#__PURE__*/React.createElement(Spacing, {
    width: 16
  }), /*#__PURE__*/React.createElement(Button, {
    disabled: selectedLinesOfCoverage.length === 0,
    loading: isSubmitting,
    onClick: () => onAddClick(selectedLinesOfCoverage)
  }, "Done"))));
}

function LineOfCoverageSelector(_ref3) {
  let {
    selectedType,
    lineageType,
    lineOfCoverageUuid,
    accountUuid,
    currLocs,
    selectedLinesOfCoverage,
    setSelectedLinesOfCoverage
  } = _ref3;
  const {
    data: possibleCandidates,
    isLoading
  } = useLineOfCoverageLineageCandidates({
    accountUuid,
    lineOfCoverageUuid,
    candidateType: lineageType ?? 'prior',
    lineOfCoverageType: selectedType === null || selectedType === void 0 ? void 0 : selectedType.id,
    localLocUuids: currLocs
  });
  const {
    results,
    search
  } = useFuzzy(possibleCandidates ?? [], {
    keys: ['lineOfCoverage.displayName', 'lineOfCoverage.definition.readableName', 'lineOfCoverage.policy.policyNumber', 'lineOfCoverage.policy.carrier.name', 'lineOfCoverage.policy.definition.readableName']
  });
  return /*#__PURE__*/React.createElement(MultiObjectSelector, {
    items: results,
    itemToString: loc => (loc === null || loc === void 0 ? void 0 : loc.lineOfCoverage.displayName) ?? '',
    getItemKey: loc => (loc === null || loc === void 0 ? void 0 : loc.lineOfCoverage.uuid) ?? '',
    renderItem: (loc, _ref4) => {
      let {
        isHovering,
        isSelected
      } = _ref4;
      return /*#__PURE__*/React.createElement(LineOfCoverageCard, {
        lineOfCoverage: loc.lineOfCoverage,
        isHighlighted: isSelected,
        isHovering: isHovering,
        cardProps: {
          hideBorder: true
        },
        defaultExpanded: false
      });
    },
    renderSelectedItems: (loc, _ref5) => {
      let {
        remove
      } = _ref5;
      return /*#__PURE__*/React.createElement(LineOfCoverageCard, {
        lineOfCoverage: loc.lineOfCoverage,
        actions: [/*#__PURE__*/React.createElement(IconButton, {
          type: "close",
          onClick: remove
        })],
        isExpandable: true,
        defaultExpanded: false,
        cardProps: {
          hideBorder: true,
          backgroundColor: colors.steel[50]
        }
      });
    },
    renderItemHover: loc => /*#__PURE__*/React.createElement("div", {
      style: {
        width: 460
      }
    }, /*#__PURE__*/React.createElement(LineOfCoverageCard, {
      defaultExpanded: true,
      lineOfCoverage: loc.lineOfCoverage
    })),
    onSelect: locs => {
      setSelectedLinesOfCoverage(locs);
    },
    selectedItems: selectedLinesOfCoverage,
    isLoading: isLoading,
    onSearch: search
  });
}

function useUpdateLocLineage() {
  const {
    useMutation,
    invalidateQuery
  } = useAdminTRPC();
  const notifications = useNotifications();
  return useMutation('loc.updateLocLineage', {
    onSuccess: async (response, _ref) => {
      let {
        lineOfCoverageUuid
      } = _ref;
      notifications.add({
        type: 'success',
        title: 'LOC lineage has been updated!'
      });
      await invalidateQuery(['loc.getByUuid', {
        lineOfCoverageUuid
      }]);
      return response;
    },
    onError: error => {
      notifications.addById('use-update-loc-lineage-hook-error', {
        type: 'error',
        title: `There was an error trying to update this LOC lineage: ${error.name}`
      });
    }
  });
}

function LineOfCoverageLineageEditGroup(_ref) {
  let {
    lineageType,
    linesOfCoverage,
    lineOfCoverageDefinition,
    lineOfCoverageUuid,
    accountUuid,
    getLineOfCoverageDetailsLink,
    getPolicyDetailsLink,
    onAddClick,
    onUnlinkClick
  } = _ref;
  const {
    data: lineageCandidateTypes
  } = useLineageCandidatesLocTypes({
    lineOfCoverageUuid,
    candidateType: lineageType ?? 'next',
    localLocUuids: linesOfCoverage.map(loc => loc.lineOfCoverage.uuid ?? '')
  });
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [toBeUnlinked, setToBeUnlinked] = useState();
  const title = `${lineageType === 'next' ? 'Next' : 'Prior'} linked lines of coverage`;
  const emptyText = `No ${lineageType} LOCs linked`;
  return /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Flexbox, {
    justifyContent: "space-between",
    width: "100%",
    marginBottom: 8
  }, /*#__PURE__*/React.createElement(Text, {
    weight: 600
  }, title), (lineageCandidateTypes === null || lineageCandidateTypes === void 0 ? void 0 : lineageCandidateTypes.length) === 0 ? /*#__PURE__*/React.createElement(Tooltip, {
    customElement: /*#__PURE__*/React.createElement(BasicTooltip, null, /*#__PURE__*/React.createElement(Flexbox, {
      alignItems: "center"
    }, /*#__PURE__*/React.createElement(IconCircleInformation, {
      color: "white"
    }), /*#__PURE__*/React.createElement(Spacing, {
      width: 8
    }), /*#__PURE__*/React.createElement(Text, {
      color: "white"
    }, "There are no available LOCs to add"))),
    positionTip: "bottom"
  }, /*#__PURE__*/React.createElement(IconButton, {
    type: "add",
    onClick: () => {
      setIsAdding(!isAdding);
    },
    disabled: true
  })) : /*#__PURE__*/React.createElement(Tooltip, {
    message: "Add line of coverage",
    positionTip: "bottom"
  }, /*#__PURE__*/React.createElement(IconButton, {
    type: "add",
    onClick: () => setIsAdding(!isAdding)
  }))), linesOfCoverage.length === 0 ? /*#__PURE__*/React.createElement(Flexbox, {
    alignItems: "center",
    justifyContent: "center",
    padding: 32
  }, /*#__PURE__*/React.createElement(Text, {
    color: colors.steel[200]
  }, emptyText)) : /*#__PURE__*/React.createElement(Stack, {
    gap: 8,
    direction: StackDirectionType.VERTICAL
  }, linesOfCoverage.map(lineOfCoverage => /*#__PURE__*/React.createElement(LineOfCoverageCard, {
    key: lineOfCoverage.lineOfCoverage.uuid ?? lineOfCoverage.lineOfCoverageLineageUuid,
    lineOfCoverage: lineOfCoverage.lineOfCoverage,
    cardProps: {
      backgroundColor: colors.steel[50],
      hideBorder: true
    },
    actions: [/*#__PURE__*/React.createElement(Tooltip, {
      message: "More details",
      positionTip: "bottom"
    }, /*#__PURE__*/React.createElement(IconButton, {
      href: getLineOfCoverageDetailsLink(lineOfCoverage.lineOfCoverage.uuid ?? '').href,
      type: "circleInformation",
      onClick: getLineOfCoverageDetailsLink(lineOfCoverage.lineOfCoverage.uuid ?? '').onClick
    })), /*#__PURE__*/React.createElement(Tooltip, {
      message: "Unlink this LOC",
      positionTip: "bottom"
    }, /*#__PURE__*/React.createElement(IconButton, {
      type: "unlink",
      onClick: () => {
        setIsUnlinking(true);
        setToBeUnlinked(lineOfCoverage);
      }
    }))],
    policyDetailsLink: getPolicyDetailsLink(lineOfCoverage.lineOfCoverage.policy.uuid ?? '')
  })))), isUnlinking && /*#__PURE__*/React.createElement(ActionModal, {
    title: "Unlink line of coverage",
    isOpen: true,
    onClose: () => setIsUnlinking(false),
    onConfirm: async () => onUnlinkClick(toBeUnlinked),
    variant: "dangerous",
    confirmText: "Unlink"
  }, /*#__PURE__*/React.createElement(Text, null, "Are you sure you want to unlink this LOC from the lineage?")), isAdding && /*#__PURE__*/React.createElement(LineOfCoverageLineageAddModal, {
    lineageType: lineageType,
    lineOfCoverageDefinition: lineageCandidateTypes !== null && lineageCandidateTypes !== void 0 && lineageCandidateTypes.map(def => def.id).includes(lineOfCoverageDefinition.id) ? lineOfCoverageDefinition : undefined,
    lineOfCoverageUuid: lineOfCoverageUuid,
    accountUuid: accountUuid,
    isOpen: true,
    toggleIsOpen: () => setIsAdding(!isAdding),
    onAddClick: locs => {
      onAddClick(locs);
      setIsAdding(!isAdding);
    },
    isSubmitting: false,
    currLocs: linesOfCoverage
  }));
}

function handleDelete(currentList, setFieldValue, field, loc) {
  if (loc) {
    setFieldValue(field, currentList.filter(lineage => lineage.lineOfCoverage.uuid !== loc.lineOfCoverage.uuid));
  }
}
function handleAdd(currentList, setFieldValue, field, locs) {
  setFieldValue(field, [...currentList, ...locs]);
}
function formatForm(values) {
  var _values$nextLinesOfCo, _values$priorLinesOfC;

  return {
    nextLinesOfCoverage: (_values$nextLinesOfCo = values.nextLinesOfCoverage) === null || _values$nextLinesOfCo === void 0 ? void 0 : _values$nextLinesOfCo.map(lineage => lineage.lineOfCoverage.uuid ?? ''),
    priorLinesOfCoverage: (_values$priorLinesOfC = values.priorLinesOfCoverage) === null || _values$priorLinesOfC === void 0 ? void 0 : _values$priorLinesOfC.map(lineage => lineage.lineOfCoverage.uuid ?? '')
  };
}

function LineOfCoverageLineageEditModal(_ref) {
  let {
    lineOfCoverageUuid,
    nextLocs,
    priorLocs,
    accountUuid,
    definition,
    isOpen,
    modalType,
    toggleIsOpen,
    getLineOfCoverageDetailsLink,
    getPolicyDetailsLink
  } = _ref;
  const {
    mutateAsync: updateLocLineage
  } = useUpdateLocLineage();
  const [isConfirmation, setIsConfirmation] = useState(false);
  const [policyClicked, setPolicyClicked] = useState('');
  const [locClicked, setLocClicked] = useState('');

  const confirmationHandler = (dirty, locUuid, policyUuid) => {
    if (dirty) {
      setIsConfirmation(true);
      setPolicyClicked(policyUuid);
      setLocClicked(locUuid);
    } else {
      toggleIsOpen();
    }
  };

  const confirmationLocDetailsLink = (dirty, locUuid) => {
    return { ...getLineOfCoverageDetailsLink({
        uuid: locUuid,
        accountUuid
      }),
      onClick: dirty ? e => {
        confirmationHandler(dirty, locUuid, '');
        e.preventDefault();
      } : getLineOfCoverageDetailsLink({
        uuid: locUuid,
        accountUuid
      }).onClick
    };
  };

  const confirmationPolicyDetailsLink = (dirty, policyUuid) => {
    return { ...getPolicyDetailsLink({
        uuid: policyUuid,
        accountUuid
      }),
      onClick: dirty ? e => {
        confirmationHandler(dirty, '', policyUuid);
        e.preventDefault();
      } : getPolicyDetailsLink({
        uuid: policyUuid,
        accountUuid
      }).onClick
    };
  };

  const initialFormValues = {
    nextLinesOfCoverage: nextLocs,
    priorLinesOfCoverage: priorLocs
  };
  return /*#__PURE__*/React.createElement(Formik, {
    onSubmit: async values => {
      await updateLocLineage({
        lineOfCoverageUuid,
        form: formatForm(values)
      });
      toggleIsOpen();
    },
    initialValues: initialFormValues
  }, _ref2 => {
    let {
      handleSubmit,
      values,
      setFieldValue,
      isSubmitting,
      dirty
    } = _ref2;
    return /*#__PURE__*/React.createElement(Modal, {
      isOpen: isOpen,
      onToggle: () => confirmationHandler(dirty, '', '')
    }, /*#__PURE__*/React.createElement(ModalBody, null, /*#__PURE__*/React.createElement(ModalHeader, {
      titleText: modalType === 'edit' ? 'Edit LOC lineage' : 'View all',
      onClose: () => confirmationHandler(dirty, '', '')
    }), /*#__PURE__*/React.createElement("form", {
      onSubmit: handleSubmit
    }, /*#__PURE__*/React.createElement(Padding, {
      size: 32
    }, (modalType === 'edit' || modalType === 'prior') && /*#__PURE__*/React.createElement(LineOfCoverageLineageEditGroup, {
      lineageType: "prior",
      linesOfCoverage: values.priorLinesOfCoverage ?? [],
      onAddClick: locs => {
        handleAdd(values.priorLinesOfCoverage ?? [], setFieldValue, 'priorLinesOfCoverage', locs);
      },
      onUnlinkClick: loc => {
        handleDelete(values.priorLinesOfCoverage ?? [], setFieldValue, 'priorLinesOfCoverage', loc);
      },
      getLineOfCoverageDetailsLink: locUuid => confirmationLocDetailsLink(dirty, locUuid),
      getPolicyDetailsLink: policyUuid => confirmationPolicyDetailsLink(dirty, policyUuid),
      lineOfCoverageDefinition: definition,
      lineOfCoverageUuid: lineOfCoverageUuid ?? '',
      accountUuid: accountUuid
    }), modalType === 'edit' && /*#__PURE__*/React.createElement(Spacing, {
      height: 24
    }), (modalType === 'edit' || modalType === 'next') && /*#__PURE__*/React.createElement(LineOfCoverageLineageEditGroup, {
      lineageType: "next",
      linesOfCoverage: values.nextLinesOfCoverage ?? [],
      onAddClick: locs => {
        handleAdd(values.nextLinesOfCoverage ?? [], setFieldValue, 'nextLinesOfCoverage', locs);
      },
      onUnlinkClick: loc => {
        handleDelete(values.nextLinesOfCoverage ?? [], setFieldValue, 'nextLinesOfCoverage', loc);
      },
      getLineOfCoverageDetailsLink: locUuid => confirmationLocDetailsLink(dirty, locUuid),
      getPolicyDetailsLink: policyUuid => confirmationPolicyDetailsLink(dirty, policyUuid),
      lineOfCoverageDefinition: definition,
      lineOfCoverageUuid: lineOfCoverageUuid ?? '',
      accountUuid: accountUuid
    })), /*#__PURE__*/React.createElement(ModalFooter, null, /*#__PURE__*/React.createElement(Button, {
      type: "button",
      size: "secondary",
      onClick: () => confirmationHandler(dirty, '', '')
    }, "Cancel"), /*#__PURE__*/React.createElement(Spacing, {
      width: 12
    }), /*#__PURE__*/React.createElement(Button, {
      type: "submit",
      disabled: isSubmitting || !dirty,
      loading: isSubmitting
    }, "Save")))), isConfirmation && /*#__PURE__*/React.createElement(ConfirmationModal, {
      onClose: () => setIsConfirmation(false),
      locUuid: locClicked,
      policyUuid: policyClicked,
      accountUuid: accountUuid,
      getLocDetails: getLineOfCoverageDetailsLink,
      getPolicyDetails: getPolicyDetailsLink,
      toggleModal: toggleIsOpen
    }));
  });
}
function ConfirmationModal(_ref3) {
  let {
    locUuid,
    policyUuid,
    accountUuid,
    getPolicyDetails,
    getLocDetails,
    onClose,
    toggleModal
  } = _ref3;
  const locClicked = locUuid ? getLocDetails({
    uuid: locUuid,
    accountUuid
  }).onClick : undefined;
  const policyClicked = policyUuid ? getPolicyDetails({
    uuid: policyUuid,
    accountUuid
  }).onClick : undefined;
  return /*#__PURE__*/React.createElement(Modal, {
    isOpen: true,
    onToggle: onClose,
    width: "550px"
  }, /*#__PURE__*/React.createElement(ModalBody, null, /*#__PURE__*/React.createElement(ModalHeader, {
    showBorder: false,
    titleText: "Unsaved changes",
    onClose: () => onClose()
  }), /*#__PURE__*/React.createElement(Padding, {
    x: 24
  }, /*#__PURE__*/React.createElement(Text, null, "Are you sure you want to continue? You will lose all unsaved changes.")), /*#__PURE__*/React.createElement(ModalFooter, {
    showBorder: false
  }, /*#__PURE__*/React.createElement(Stack, {
    gap: 8
  }, /*#__PURE__*/React.createElement(Button, {
    onClick: onClose,
    size: "secondary"
  }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
    size: "dangerous",
    onClick: e => {
      if (locClicked) {
        locClicked(e);
      } else if (policyClicked) {
        policyClicked(e);
      } else {
        toggleModal();
      }

      onClose();
    }
  }, "Discard changes")))));
}

function LineOfCoverageLineageStackedCard(_ref) {
  let {
    emptyText,
    emptyLinkText = 'Link LOC',
    emptyLinkOnClick,
    onViewAllClick,
    children
  } = _ref;

  if (children.length === 0) {
    return /*#__PURE__*/React.createElement(Flexbox, {
      width: "100%",
      justifyContent: "center",
      alignItems: "center",
      flexDirection: "column"
    }, /*#__PURE__*/React.createElement(Text, null, emptyText), /*#__PURE__*/React.createElement(TextLink, {
      style: {
        marginTop: '5px'
      },
      onClick: emptyLinkOnClick
    }, emptyLinkText));
  }

  if (children.length === 1) {
    return /*#__PURE__*/React.createElement(Flexbox, {
      width: "100%"
    }, children[0]);
  }

  return /*#__PURE__*/React.createElement(Flexbox, {
    flexDirection: "column",
    width: "100%"
  }, /*#__PURE__*/React.createElement(StyledDiv, {
    onClick: onViewAllClick,
    style: {
      cursor: 'pointer'
    }
  }, /*#__PURE__*/React.createElement(Tooltip, {
    message: `View all ${children.length} lines of coverage`,
    followMouse: true,
    positionTip: "bottom"
  }, children[0])), /*#__PURE__*/React.createElement(Flexbox, {
    alignSelf: "flex-end"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "small",
    color: colors.steel[400]
  }, children.length, " lines of coverage in total."), /*#__PURE__*/React.createElement(TextLink, {
    size: "small",
    onClick: onViewAllClick,
    style: {
      marginLeft: '5px'
    },
    underline: false
  }, "View all")));
}
const StyledDiv = styled.div`
  margin: 0 20px 30px 0;
  box-shadow: 3px 3px var(--nfui-modal-background), 10px 10px var(--nfui-card-shadow),
    13px 13px var(--nfui-modal-background), 20px 20px var(--nfui-card-shadow);
  border-radius: 4px;
  transition: 0.3s;

  &:hover {
    box-shadow: 3px 3px var(--nfui-modal-background), 10px 13px var(--nfui-card-shadow),
      13px 16px var(--nfui-modal-background), 20px 26px var(--nfui-card-shadow);
  }
`;

function LineOfCoverageLineage(_ref) {
  let {
    lineOfCoverage,
    getLineOfCoverageDetailsLink,
    getPolicyDetailsLink
  } = _ref;
  const {
    mutateAsync: updateLocLineage,
    isLoading
  } = useUpdateLocLineage();
  const [modalType, setModalType] = useState(undefined);
  const [isAdding, setIsAdding] = useState(undefined);

  if (!lineOfCoverage) {
    return null;
  }

  const {
    uuid: lineOfCoverageUuid,
    priorInLineage,
    nextInLineage,
    definition,
    accountUuid
  } = lineOfCoverage;

  const handleAdd = async locs => {
    await updateLocLineage({
      lineOfCoverageUuid: lineOfCoverageUuid ?? '',
      form: isAdding === 'next' ? {
        nextLinesOfCoverage: locs.map(loc => loc.lineOfCoverage.uuid ?? '')
      } : {
        priorLinesOfCoverage: locs.map(loc => loc.lineOfCoverage.uuid ?? '')
      }
    });
    setIsAdding(undefined);
  };

  return /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(ExpandableModule, {
    label: "LOC lineage",
    editIcon: "iconPencil",
    onEdit: () => setModalType('edit'),
    defaultExpanded: true
  }, /*#__PURE__*/React.createElement(Flexbox, {
    display: "flex",
    flexDirection: "row"
  }, /*#__PURE__*/React.createElement(Flexbox, {
    flex: 1
  }, /*#__PURE__*/React.createElement(LineOfCoverageLineageStackedCard, {
    emptyText: "No prior LOC linked",
    emptyLinkOnClick: () => setIsAdding('prior'),
    onViewAllClick: () => setModalType('prior')
  }, priorInLineage.map(lineageItem => /*#__PURE__*/React.createElement(Box, {
    width: "100%",
    key: lineageItem.lineOfCoverageLineageUuid
  }, /*#__PURE__*/React.createElement(LineOfCoverageCard, {
    lineOfCoverage: lineageItem.lineOfCoverage,
    cardProps: {
      backgroundColor: colors.steel[50],
      hideBorder: true
    },
    hoverProps: priorInLineage.length !== 1 ? {
      hoverBackgroundColor: colors.brand[100]
    } : undefined,
    actions: priorInLineage.length === 1 ? [/*#__PURE__*/React.createElement(Tooltip, {
      message: "More details",
      positionTip: "bottom"
    }, /*#__PURE__*/React.createElement(IconButton, {
      href: getLineOfCoverageDetailsLink(lineageItem.lineOfCoverage).href,
      type: "circleInformation",
      onClick: () => getLineOfCoverageDetailsLink(lineageItem.lineOfCoverage).onClick
    }))] : undefined,
    policyDetailsLink: priorInLineage.length === 1 ? getPolicyDetailsLink(lineageItem.lineOfCoverage.policy) : undefined
  }))))), /*#__PURE__*/React.createElement(Flexbox, {
    flex: 1,
    paddingLeft: 5,
    paddingRight: 5
  }, /*#__PURE__*/React.createElement(Box, {
    width: "100%"
  }, /*#__PURE__*/React.createElement(LineOfCoverageCard, {
    lineOfCoverage: lineOfCoverage
  }))), /*#__PURE__*/React.createElement(Flexbox, {
    flex: 1
  }, /*#__PURE__*/React.createElement(LineOfCoverageLineageStackedCard, {
    emptyText: "No next LOC linked",
    emptyLinkOnClick: () => setIsAdding('next'),
    onViewAllClick: () => setModalType('next')
  }, nextInLineage.map(lineageItem => /*#__PURE__*/React.createElement(Box, {
    width: "100%",
    key: lineageItem.lineOfCoverageLineageUuid
  }, /*#__PURE__*/React.createElement(LineOfCoverageCard, {
    lineOfCoverage: lineageItem.lineOfCoverage,
    cardProps: {
      backgroundColor: colors.steel[50],
      hideBorder: true
    },
    hoverProps: nextInLineage.length !== 1 ? {
      hoverBackgroundColor: colors.brand[100]
    } : undefined,
    actions: nextInLineage.length === 1 ? [/*#__PURE__*/React.createElement(Tooltip, {
      message: "More details",
      positionTip: "bottom"
    }, /*#__PURE__*/React.createElement(IconButton, {
      href: getLineOfCoverageDetailsLink(lineageItem.lineOfCoverage).href,
      type: "circleInformation",
      onClick: () => getLineOfCoverageDetailsLink(lineageItem.lineOfCoverage).onClick
    }))] : undefined,
    policyDetailsLink: nextInLineage.length === 1 ? getPolicyDetailsLink(lineageItem.lineOfCoverage.policy) : undefined
  }))))))), modalType && /*#__PURE__*/React.createElement(LineOfCoverageLineageEditModal, {
    isOpen: true,
    toggleIsOpen: () => setModalType(undefined),
    modalType: modalType ?? 'edit',
    lineOfCoverageUuid: lineOfCoverageUuid ?? '',
    nextLocs: nextInLineage,
    priorLocs: priorInLineage,
    definition: definition,
    accountUuid: accountUuid,
    getLineOfCoverageDetailsLink: getLineOfCoverageDetailsLink,
    getPolicyDetailsLink: getPolicyDetailsLink
  }), isAdding && /*#__PURE__*/React.createElement(LineOfCoverageLineageAddModal, {
    lineageType: isAdding,
    lineOfCoverageDefinition: definition,
    lineOfCoverageUuid: lineOfCoverageUuid ?? '',
    accountUuid: accountUuid,
    isOpen: true,
    isSubmitting: isLoading,
    toggleIsOpen: () => setIsAdding(undefined),
    onAddClick: handleAdd,
    currLocs: []
  }));
}

function useScheduledLimitsMetadata(params, options) {
  const {
    useQuery
  } = useAdminTRPC();
  return useQuery(['scheduledLimits.getScheduledLimitsDataAndMetadata', params], options);
}

function useScheduledLimitsReadableValues(params, options) {
  const {
    useQuery
  } = useAdminTRPC();
  return useQuery(['scheduledLimits.getReadableValues', params], options);
}

function ScheduledLimitDeleteModal(_ref) {
  let {
    scheduledLimit,
    onToggle,
    lineOfCoverageType,
    isInsurableItemContext = false
  } = _ref;
  const {
    useMutation,
    invalidateQuery
  } = useAdminTRPC();
  const notifications = useNotifications();
  const {
    mutateAsync: deleteScheduledLimit
  } = useMutation('scheduledLimits.deleteScheduledLimit', {
    onSuccess: async () => {
      await invalidateQuery([isInsurableItemContext ? 'scheduledLimits.getScheduledLimitsDataForII' : 'scheduledLimits.getScheduledLimitsDataAndMetadata'], {
        exact: false
      });
      onToggle();
      notifications.add({
        type: 'success',
        title: 'Scheduled limit succesfully deleted...'
      });
    }
  }); // Only blanket limits can exist without a scheduledItemUuid

  const isBlanket = !scheduledLimit.scheduledItemUuid;
  const {
    data,
    isLoading
  } = useScheduledLimitsReadableValues({
    scheduledLimitType: scheduledLimit.type,
    insurableItemUuid: scheduledLimit.insurableItemUuid,
    lineOfCoverageType
  }, {
    enabled: !isBlanket
  });

  const handleDelete = async () => {
    await deleteScheduledLimit({
      scheduledLimitUuid: scheduledLimit.uuid,
      scheduledLimitType: scheduledLimit.type,
      lineOfCoverageUuid: scheduledLimit.lineOfCoverageUuid
    });
  };

  return /*#__PURE__*/React.createElement(ActionModal, {
    variant: "dangerous",
    title: "Delete scheduled coverage from the item",
    isOpen: true,
    confirmText: "Delete",
    onClose: onToggle,
    onConfirm: () => handleDelete()
  }, isLoading && !data ? /*#__PURE__*/React.createElement(CircleSpinnerInline, {
    label: "Loading Schedule limit data..."
  }) : /*#__PURE__*/React.createElement(Text, null, "Are you sure you want delete", ' ', data !== null && data !== void 0 && data.scheduledLimitDisplayName ? /*#__PURE__*/React.createElement(Text, {
    weight: 600,
    display: "inline"
  }, data === null || data === void 0 ? void 0 : data.scheduledLimitDisplayName) : /*#__PURE__*/React.createElement(Text, {
    display: "inline"
  }, "this blanket limit"), data !== null && data !== void 0 && data.insurableItemIdentifier ? /*#__PURE__*/React.createElement(React.Fragment, null, ' ', "from", ' ', /*#__PURE__*/React.createElement(Text, {
    weight: 600,
    display: "inline"
  }, data === null || data === void 0 ? void 0 : data.insurableItemIdentifier)) : null, "?"));
}

/** Taken from https://formik.org/docs/api/fieldarray#fieldarray-validation-gotchas */
function getFormikErrorFromArray(error) {
  if (!error) {
    return error;
  }

  const parsedError = typeof error === 'string' ? error : String(error === null || error === void 0 ? void 0 : error[0]);
  return parsedError;
}

function useInsurableValuesFromInsurableItems(params, options) {
  const {
    useQuery
  } = useAdminTRPC();
  return useQuery(['insurableValues.getInsurableValuesByInsurableItems', params], options);
}

function InsurableValuesSelectList(_ref) {
  var _errors$name;

  let {
    insurableItemUuids,
    accountUuid,
    insurableItemType,
    name
  } = _ref;
  // query the insurable values with it's values
  const {
    data
  } = useInsurableValuesFromInsurableItems({
    insurableItemUuids,
    accountUuid,
    insurableItemType
  }, {
    suspense: true
  });
  const {
    errors,
    values: formValues
  } = useFormikContext(); // There should only be one error for this array field

  const convertedError = getFormikErrorFromArray((_errors$name = errors[name]) === null || _errors$name === void 0 ? void 0 : _errors$name.insurableValuesSnapshot);
  return /*#__PURE__*/React.createElement(Stack, {
    direction: "vertical",
    gap: 16
  }, /*#__PURE__*/React.createElement(Field, {
    hideEmptyLabel: true,
    error: convertedError,
    fieldId: `${name}-insurableValuesSnapshot`
  }, /*#__PURE__*/React.createElement(FieldArray, {
    name: `${name}.insurableValuesSnapshot`
  }, arrayHelpers => ((data === null || data === void 0 ? void 0 : data.insurableValuesResponse) || []).map(item => {
    return /*#__PURE__*/React.createElement(Flexbox, {
      justifyContent: "space-between",
      alignItems: "flex-start",
      key: item.definition.propertyName
    }, /*#__PURE__*/React.createElement(CheckboxRow, {
      label: item.label,
      onClick: isChecked => {
        // eslint-disable-next-line no-unused-expressions
        isChecked ? arrayHelpers.push({
          propertyName: item.definition.propertyName,
          insurableValue: item.insurableValue
        }) : arrayHelpers.remove(formValues[name].insurableValuesSnapshot.findIndex(formValue => item.definition.propertyName === formValue.propertyName));
      },
      isActive: formValues[name].insurableValuesSnapshot.some(value => value.propertyName === item.definition.propertyName)
    }), /*#__PURE__*/React.createElement(Text, null, "Valued at ", moneyToString(item.insurableValue)));
  }))));
}

function useEnrichedScheduleLimitsDefinitions(params, options) {
  const {
    useQuery
  } = useAdminTRPC();
  return useQuery(['scheduledLimits.getScheduleLimitsDefinitions', params], options);
}

function ScheduleLimitDslForm(_ref) {
  let {
    insurableItemType,
    lineOfCoverageType,
    scheduledLimitType,
    name
  } = _ref;
  return /*#__PURE__*/React.createElement(Suspense, {
    fallback: /*#__PURE__*/React.createElement(Flexbox, {
      justifyContent: "flex-start",
      alignItems: "center"
    }, /*#__PURE__*/React.createElement(CircleSpinnerInline, {
      label: "Loading form..."
    }))
  }, /*#__PURE__*/React.createElement(ScheduleLimitDslFormInner, {
    insurableItemType: insurableItemType,
    lineOfCoverageType: lineOfCoverageType,
    scheduledLimitType: scheduledLimitType,
    name: name
  }));
}
function ScheduleLimitDslFormInner(_ref2) {
  let {
    insurableItemType,
    lineOfCoverageType,
    scheduledLimitType,
    name
  } = _ref2;
  const {
    data
  } = useEnrichedScheduleLimitsDefinitions({
    insurableItemType,
    lineOfCoverageType,
    scheduledLimitType
  }, {
    suspense: true
  });

  if (!data) {
    return null;
  }

  const {
    scheduleLimitResponse
  } = data;
  return /*#__PURE__*/React.createElement(FormikSchemaInput, {
    name: `${name}.data`,
    schema: scheduleLimitResponse.schema
  });
}

function useAvailableScheduleLimitsDefinitionsByLoc(params, options) {
  const {
    useQuery
  } = useAdminTRPC();
  return useQuery(['scheduledLimits.getAvailableScheduleLimitsDefinitionsByLoc', params], options);
}

function ScheduledLimitsTypeSelector(_ref) {
  let {
    lineOfCoverageType,
    lineOfCoverageUuid,
    scheduledItemUuid,
    onChange,
    value
  } = _ref;
  const {
    data,
    isLoading
  } = useAvailableScheduleLimitsDefinitionsByLoc({
    lineOfCoverageType,
    lineOfCoverageUuid,
    scheduledItemUuid
  }, {
    // we want to always take the fresh data from the server
    cacheTime: 0
  });

  const handleChange = val => {
    const item = ((data === null || data === void 0 ? void 0 : data.scheduleLimitsDefinitionsResponse) ?? []).find(option => option.id === val);
    onChange(item);
  };

  return /*#__PURE__*/React.createElement(AdvancedSelectInput, {
    isClearable: true,
    options: (data === null || data === void 0 ? void 0 : data.scheduleLimitsDefinitionsResponse) ?? [],
    onChange: handleChange,
    value: value,
    isLoading: isLoading
  });
}

function useMultiSelectorData(data) {
  const [filteredData, setFilteredData] = useState(data);

  const renderOptions = option => {
    return /*#__PURE__*/React.createElement(Padding, {
      y: 20
    }, /*#__PURE__*/React.createElement(Text, {
      weight: 600,
      color: option.isDisabled ? colors.steel[200] : 'primary'
    }, option.label), /*#__PURE__*/React.createElement(Text, {
      color: option.isDisabled ? colors.steel[200] : 'primary'
    }, option.description || option.readableType));
  };

  const renderSelected = option => {
    return /*#__PURE__*/React.createElement(Padding, {
      size: 20
    }, /*#__PURE__*/React.createElement(Text, {
      weight: 600
    }, option.label), /*#__PURE__*/React.createElement(Text, null, option.readableType));
  };

  const searchFunction = searchQuery => {
    if (!searchQuery) {
      setFilteredData(data);
      return;
    }

    const result = data.filter(item => item.label.toLowerCase().includes(searchQuery.toLowerCase()));
    setFilteredData(result);
  };

  return {
    filteredData,
    renderOptions,
    renderSelected,
    searchFunction
  };
}

/* eslint-disable prettier/prettier */
function ScheduledLimitsAddForm(_ref) {
  var _errors$name, _errors$name2, _values$name$schedule, _values$name$schedule2, _values$name$schedule3, _values$name$schedule4, _values$name, _values$name$schedule5, _selectedScheduleLimi2;

  let {
    lineOfCoverageType,
    lineOfCoverageUuid,
    accountUuid,
    name,
    insurableItemOption,
    handleEdit
  } = _ref;
  const {
    values,
    setFieldValue,
    setValues,
    errors
  } = useFormikContext();
  const selectedScheduleLimit = values[name].scheduledLimitType;
  const {
    useQuery
  } = useAdminTRPC();
  const {
    data,
    isLoading
  } = useQuery(['scheduledLimits.getAvailableInsurableItems', {
    accountUuid,
    lineOfCoverageUuid,
    insurableItemType: values[name].insurableItemType,
    scheduledLimitType: selectedScheduleLimit === null || selectedScheduleLimit === void 0 ? void 0 : selectedScheduleLimit.scheduledLimitType,
    insurableItemOptionUuid: insurableItemOption === null || insurableItemOption === void 0 ? void 0 : insurableItemOption.value
  }], {
    enabled: !!selectedScheduleLimit && !!values[name].insurableItemType,
    cacheTime: 0
  });
  const initialData = data || [];
  const {
    filteredData,
    renderOptions,
    renderSelected,
    searchFunction
  } = useMultiSelectorData(initialData);
  const insurableItemsError = getFormikErrorFromArray((_errors$name = errors[name]) === null || _errors$name === void 0 ? void 0 : _errors$name.insurableItems);
  return /*#__PURE__*/React.createElement(Stack, {
    gap: 24,
    direction: "vertical"
  }, /*#__PURE__*/React.createElement(Field, {
    label: "Select scheduled coverage*",
    fieldId: `${name}.type`,
    error: (_errors$name2 = errors[name]) === null || _errors$name2 === void 0 ? void 0 : _errors$name2.scheduledLimitType
  }, /*#__PURE__*/React.createElement(ScheduledLimitsTypeSelector, {
    lineOfCoverageType: lineOfCoverageType,
    lineOfCoverageUuid: lineOfCoverageUuid,
    scheduledItemUuid: insurableItemOption === null || insurableItemOption === void 0 ? void 0 : insurableItemOption.scheduledItemUuid,
    onChange: scheduleLimitType => {
      var _selectedScheduleLimi;

      const insurableItemType = selectedScheduleLimit !== null && selectedScheduleLimit !== void 0 && (_selectedScheduleLimi = selectedScheduleLimit.insurableItemOptions) !== null && _selectedScheduleLimi !== void 0 && _selectedScheduleLimi.length ? '' : scheduleLimitType.insurableItemType;
      setValues({ ...values,
        [name]: { ...values[name],
          scheduledLimitType: scheduleLimitType,
          insurableItems: insurableItemOption ? [insurableItemOption] : [],
          scheduledItemUuid: '',
          insurableItemType
        }
      });
    },
    value: ((_values$name$schedule = values[name].scheduledLimitType) === null || _values$name$schedule === void 0 ? void 0 : _values$name$schedule.id) ?? ''
  })), selectedScheduleLimit !== null && selectedScheduleLimit !== void 0 && selectedScheduleLimit.blanketable ? /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(CheckboxRow, {
    label: "This is a blanket coverage",
    isActive: !!values[name].isBlanket && !((_values$name$schedule2 = values[name].scheduledLimitType) !== null && _values$name$schedule2 !== void 0 && _values$name$schedule2.currentBlanket),
    disabled: !!((_values$name$schedule3 = values[name].scheduledLimitType) !== null && _values$name$schedule3 !== void 0 && _values$name$schedule3.currentBlanket),
    onClick: isChecked => setFieldValue(`${name}.isBlanket`, isChecked)
  }), (_values$name$schedule4 = values[name].scheduledLimitType) !== null && _values$name$schedule4 !== void 0 && _values$name$schedule4.currentBlanket ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Spacing, {
    height: 8
  }), /*#__PURE__*/React.createElement(Flexbox, {
    alignItems: "center",
    gap: 8
  }, /*#__PURE__*/React.createElement(IconCircleInformation, {
    color: colors.brand[300],
    size: 16
  }), /*#__PURE__*/React.createElement(Text, {
    color: colors.brand[400],
    size: "small"
  }, "Blanket coverage for ", (_values$name = values[name]) === null || _values$name === void 0 ? void 0 : (_values$name$schedule5 = _values$name.scheduledLimitType) === null || _values$name$schedule5 === void 0 ? void 0 : _values$name$schedule5.readableName, ' ', "for this LOC already exists.", ' ', /*#__PURE__*/React.createElement(TextLink, {
    weight: 600,
    size: "small",
    onClick: () => {
      var _values$name$schedule6;

      return handleEdit((_values$name$schedule6 = values[name].scheduledLimitType) === null || _values$name$schedule6 === void 0 ? void 0 : _values$name$schedule6.currentBlanket);
    }
  }, "Edit existing")))) : null) : null, selectedScheduleLimit !== null && selectedScheduleLimit !== void 0 && (_selectedScheduleLimi2 = selectedScheduleLimit.insurableItemOptions) !== null && _selectedScheduleLimi2 !== void 0 && _selectedScheduleLimi2.length ? /*#__PURE__*/React.createElement(Field, {
    label: "Select scheduled item type*",
    fieldId: `${name}.insurableItemType`
  }, /*#__PURE__*/React.createElement(AdvancedSelectInput, {
    isClearable: true,
    options: selectedScheduleLimit.insurableItemOptions ?? [],
    onChange: value => {
      setFieldValue(`${name}.insurableItems`, []);
      setFieldValue(`${name}.insurableItemType`, value);
    },
    value: values[name].insurableItemType
  })) : null, insurableItemOption && values[name].isBlanket ? /*#__PURE__*/React.createElement(Field, {
    label: "Select other scheduled items*",
    fieldId: `${name}.scheduleCoverageType`,
    error: insurableItemsError
  }, /*#__PURE__*/React.createElement(MultiObjectSelect, {
    renderOptions: renderOptions,
    renderSelected: renderSelected,
    searchFetch: searchFunction,
    showOptionsOnEmpty: true,
    data: filteredData,
    onSelectValue: values => {
      setFieldValue(`${name}.insurableItems`, [insurableItemOption, ...Object.values(values)]);
    },
    selectedItems: toRecord(values[name].insurableItems.filter(insurableItem => insurableItem.value !== insurableItemOption.value) || [], insurableItem => insurableItem.value),
    formatter: result => {
      return {
        title: result.label,
        key: result.value
      };
    }
  })) : null, !insurableItemOption ? /*#__PURE__*/React.createElement(Field, {
    label: "Select scheduled items*",
    fieldId: `${name}.scheduleCoverageType`,
    error: insurableItemsError
  }, values[name].isBlanket ? /*#__PURE__*/React.createElement(MultiObjectSelect, {
    renderOptions: renderOptions,
    renderSelected: renderSelected,
    searchFetch: searchFunction,
    showOptionsOnEmpty: true,
    data: filteredData,
    onSelectValue: values => {
      setFieldValue(`${name}.insurableItems`, Object.values(values));
    },
    selectedItems: toRecord(values[name].insurableItems || [], insurableItem => insurableItem.value),
    formatter: result => {
      return {
        title: result.label,
        key: result.value
      };
    }
  }) : /*#__PURE__*/React.createElement(AdvancedSelectInput, {
    isClearable: true,
    disabled: !selectedScheduleLimit,
    isLoading: isLoading,
    options: data ?? [],
    onChange: value => {
      const item = (data ?? []).find(option => option.value === value);
      setValues({ ...values,
        [name]: { ...values[name],
          insurableItems: item ? [item] : [],
          scheduledItemUuid: (item === null || item === void 0 ? void 0 : item.scheduledItemUuid) ?? ''
        }
      });
    },
    value: values[name].insurableItems.length ? values[name].insurableItems[0].value : undefined
  })) : null, values[name].insurableItems.length ? values[name].insurableItemType && /*#__PURE__*/React.createElement(Suspense, {
    fallback: /*#__PURE__*/React.createElement(CircleSpinnerInline, {
      label: "Loading insurable values..."
    })
  }, /*#__PURE__*/React.createElement(InsurableValuesSelectList, {
    name: name,
    accountUuid: accountUuid,
    insurableItemType: values[name].insurableItemType,
    insurableItemUuids: values[name].insurableItems.map(insurableItem => insurableItem.value)
  })) : null, selectedScheduleLimit && values[name].insurableItemType && /*#__PURE__*/React.createElement(ScheduleLimitDslForm, {
    insurableItemType: values[name].insurableItemType,
    lineOfCoverageType: lineOfCoverageType,
    scheduledLimitType: selectedScheduleLimit.scheduledLimitType,
    name: name
  }));
}

function getScheduledItemsFormInitialValues(insurableItemsToAdd, lineOfCoverage) {
  const insurableItemsToLink = insurableItemsToAdd.map(insurableItem => {
    return {
      insurableItemUuid: insurableItem.value,
      label: insurableItem.label,
      readableType: insurableItem.readableType,
      startDate: lineOfCoverage.startDate,
      endDate: lineOfCoverage.endDate
    };
  });
  return {
    insurableItemsToLink
  };
}

function getAddScheduledItemValidation() {
  return yup.object({
    insurableItemsToLink: yup.array().of(yup.object().shape({
      startDate: yup.string().required()
    }))
  });
}

function ScheduledItemRow(_ref) {
  let {
    lineOfCoverage,
    index,
    arrayHelpers
  } = _ref;
  const {
    values,
    errors
  } = useFormikContext();
  const insurableItemsToLink = values.insurableItemsToLink[index];
  const convertedError = getFormikErrorFromArray(errors.insurableItemsToLink);
  return /*#__PURE__*/React.createElement(Box, {
    backgroundColor: colors.steel[50]
  }, /*#__PURE__*/React.createElement(Padding, {
    size: 16
  }, /*#__PURE__*/React.createElement(Text, {
    weight: 600
  }, insurableItemsToLink.label), /*#__PURE__*/React.createElement(Text, null, insurableItemsToLink.readableType), /*#__PURE__*/React.createElement(Spacing, {
    height: 16
  }), /*#__PURE__*/React.createElement(Flexbox, {
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(Field, {
    fieldId: "start-date",
    label: "Coverage start date*",
    error: convertedError
  }, /*#__PURE__*/React.createElement(DatePickerInput2.Controlled, {
    value: insurableItemsToLink.startDate,
    onChange: value => arrayHelpers.replace(index, { ...insurableItemsToLink,
      startDate: value
    }),
    minDate: String(lineOfCoverage.startDate),
    maxDate: String(lineOfCoverage.endDate),
    timezone: "UTC",
    format: "MM/DD/YYYY"
  })), /*#__PURE__*/React.createElement(Field, {
    fieldId: "end-date",
    label: "Coverage end date*"
  }, /*#__PURE__*/React.createElement(DatePickerInput2.Controlled, {
    value: insurableItemsToLink.endDate,
    onChange: value => arrayHelpers.replace(index, { ...insurableItemsToLink,
      endDate: value
    }),
    timezone: "UTC",
    minDate: String(lineOfCoverage.startDate),
    maxDate: String(lineOfCoverage.endDate),
    format: "MM/DD/YYYY"
  })))));
}

function ScheduledItemsModal(_ref) {
  let {
    onToggle,
    lineOfCoverage,
    insurableItemsWithNoSchedule,
    onSubmit
  } = _ref;
  const initialValues = getScheduledItemsFormInitialValues(insurableItemsWithNoSchedule, lineOfCoverage);
  const validationSchema = getAddScheduledItemValidation();
  const [hasSubmitted, setHasSubmitted] = useState(false);
  return /*#__PURE__*/React.createElement(Modal, {
    isOpen: true,
    onToggle: onToggle,
    width: "450px"
  }, /*#__PURE__*/React.createElement(ModalBody, null, /*#__PURE__*/React.createElement(ModalHeader, {
    onClose: onToggle,
    titleText: "Add dates for scheduled items"
  }), /*#__PURE__*/React.createElement(MessageBox, {
    borderless: true,
    variant: "neutral" // eslint-disable-next-line max-len
    ,
    message: `You added limits for ${insurableItemsWithNoSchedule.length} new ${pluralize('item', insurableItemsWithNoSchedule.length)} that ${insurableItemsWithNoSchedule.length > 1 ? "haven't" : "hasn't"} been scheduled on this LOC yet, 
          please add effective dates for ${pluralize('this', insurableItemsWithNoSchedule.length)} ${pluralize('item', insurableItemsWithNoSchedule.length)} below.`
  }), /*#__PURE__*/React.createElement(Formik, {
    initialValues: initialValues,
    validationSchema: validationSchema,
    validateOnChange: hasSubmitted,
    onSubmit: onSubmit
  }, _ref2 => {
    let {
      handleSubmit,
      isSubmitting
    } = _ref2;
    return /*#__PURE__*/React.createElement("form", {
      onSubmit: handleSubmit
    }, /*#__PURE__*/React.createElement(Padding, {
      x: 24,
      top: 32,
      bottom: 24
    }, /*#__PURE__*/React.createElement(Stack, {
      direction: "vertical",
      gap: 16
    }, /*#__PURE__*/React.createElement(FieldArray, {
      name: "insurableItemsToLink"
    }, arrayHelpers => insurableItemsWithNoSchedule === null || insurableItemsWithNoSchedule === void 0 ? void 0 : insurableItemsWithNoSchedule.map((insurableItem, index) => {
      return /*#__PURE__*/React.createElement(ScheduledItemRow, {
        key: insurableItem.value,
        lineOfCoverage: lineOfCoverage,
        index: index,
        arrayHelpers: arrayHelpers
      });
    })))), /*#__PURE__*/React.createElement(ModalFooter, null, /*#__PURE__*/React.createElement(Flexbox, {
      gap: 8,
      justifyContent: "flex-end"
    }, /*#__PURE__*/React.createElement(Button, {
      size: "secondary",
      type: "button",
      onClick: onToggle
    }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
      loading: isSubmitting,
      onClick: () => {
        setHasSubmitted(true);
      }
    }, "Add"))));
  })));
}

function getScheduleLimitInitialValues(lineOfCoverage, name, insurableItemOption) {
  const {
    accountUuid,
    type: lineOfCoverageType,
    policyUuid,
    startDate,
    endDate,
    uuid: lineOfCoverageUuid
  } = lineOfCoverage;
  return {
    [name]: {
      policyUuid,
      scheduledItemUuid: '',
      insurableItems: insurableItemOption ? [insurableItemOption] : [],
      priorScheduledLimitUuid: '',
      endDate: endDate,
      startDate: startDate,
      lineOfCoverageType,
      lineOfCoverageUuid: lineOfCoverageUuid,
      data: {},
      version: 1,
      insurableValuesSnapshot: [],
      accountUuid,
      isBlanket: false,
      scheduledLimitType: undefined,
      insurableItemType: undefined
    }
  };
}

function getValidationSchema$1(name, hasInsurableItem) {
  return yup.object({
    [name]: yup.object({
      lineOfCoverageType: yup.string().required('At least one certificate holder name is required'),
      insurableItems: hasInsurableItem ? yup.array() : yup.array().min(1, 'Please select a Scheduled item'),
      scheduledLimitType: yup.object().required('Please select a Scheduled coverage')
    })
  });
}

/* eslint-disable react/jsx-props-no-spreading */
const FORM_NAME$1 = 'form';
function ScheduledLimitsAddModal(_ref) {
  let {
    lineOfCoverage,
    onToggle,
    insurableItemOption,
    handleEdit
  } = _ref;
  const {
    useMutation,
    invalidateQuery
  } = useAdminTRPC();
  const {
    data: readableData,
    isLoading: isLoadingReadable
  } = useScheduledLimitsReadableValues({
    insurableItemUuid: insurableItemOption === null || insurableItemOption === void 0 ? void 0 : insurableItemOption.value,
    lineOfCoverageType: lineOfCoverage.type,
    lineOfCoverageUuid: lineOfCoverage.uuid
  }, {
    enabled: !!insurableItemOption
  });
  const {
    mutateAsync: createScheduledLimit
  } = useMutation('scheduledLimits.createScheduledLimit', {
    onSuccess: async () => {
      await invalidateQuery([insurableItemOption ? 'scheduledLimits.getScheduledLimitsDataForII' : 'scheduledLimits.getScheduledLimitsDataAndMetadata'], {
        exact: false
      });
      setInsurableItemsWithNoSchedule([]);

      if (!resetForm) {
        onToggle();
      }

      notifications.add({
        type: 'success',
        title: 'Scheduled limit succesfully created...'
      });
    }
  });
  const notifications = useNotifications();
  const initialValues = getScheduleLimitInitialValues(lineOfCoverage, FORM_NAME$1, insurableItemOption);
  const validationSchema = getValidationSchema$1(FORM_NAME$1, !!insurableItemOption);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [resetForm, setResetForm] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState({});
  const [insurableItemsWithNoSchedule, setInsurableItemsWithNoSchedule] = useState([]);

  const handleSubmitWithScheduledItems = async values => {
    var _initialFormValues$FO, _initialFormValues$FO2;

    const form = { ...initialFormValues[FORM_NAME$1],
      ...values,
      insurableItemType: ((_initialFormValues$FO = initialFormValues[FORM_NAME$1].scheduledLimitType) === null || _initialFormValues$FO === void 0 ? void 0 : _initialFormValues$FO.insurableItemType) || initialFormValues[FORM_NAME$1].insurableItemType,
      type: (_initialFormValues$FO2 = initialFormValues[FORM_NAME$1].scheduledLimitType) === null || _initialFormValues$FO2 === void 0 ? void 0 : _initialFormValues$FO2.scheduledLimitType
    };
    await createScheduledLimit(form);
  };

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Modal, {
    isOpen: true,
    onToggle: onToggle
  }, /*#__PURE__*/React.createElement(ModalBody, null, /*#__PURE__*/React.createElement(ModalHeader, {
    titleText: "Add a scheduled limit to the coverage",
    onClose: onToggle
  }), isLoadingReadable ? /*#__PURE__*/React.createElement(Flexbox, {
    height: 300,
    width: "100%",
    justifyContent: "center",
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(CircleSpinnerInline, {
    label: "Loading Scheduled limit data..."
  })) : /*#__PURE__*/React.createElement(Formik, {
    initialValues: initialValues,
    validationSchema: validationSchema,
    validateOnChange: hasSubmitted,
    onSubmit: async values => {
      const filteredInsurableItems = values[FORM_NAME$1].insurableItems.filter(insurableItem => !insurableItem.scheduledItemUuid);

      if (!filteredInsurableItems.length) {
        var _values$FORM_NAME$sch, _values$FORM_NAME$sch2;

        // If no orphan insurable items, create the scheduled limit
        const form = { ...values[FORM_NAME$1],
          insurableItemType: ((_values$FORM_NAME$sch = values[FORM_NAME$1].scheduledLimitType) === null || _values$FORM_NAME$sch === void 0 ? void 0 : _values$FORM_NAME$sch.insurableItemType) || values[FORM_NAME$1].insurableItemType,
          type: (_values$FORM_NAME$sch2 = values[FORM_NAME$1].scheduledLimitType) === null || _values$FORM_NAME$sch2 === void 0 ? void 0 : _values$FORM_NAME$sch2.scheduledLimitType
        };
        await createScheduledLimit(form);
      } else {
        // otherwise, store the original form data and show the add scheduled items modal
        setInitialFormValues(values);
        setInsurableItemsWithNoSchedule(filteredInsurableItems);
      }
    }
  }, _ref2 => {
    let {
      handleSubmit,
      isSubmitting,
      submitForm,
      resetForm,
      validateForm
    } = _ref2;
    return /*#__PURE__*/React.createElement("form", {
      onSubmit: handleSubmit
    }, insurableItemOption && /*#__PURE__*/React.createElement(MessageBox, {
      borderless: true,
      variant: "neutral",
      message: /*#__PURE__*/React.createElement(Text, {
        color: colors.brand[400]
      }, "You\u2019re adding scheduled coverage for", ' ', /*#__PURE__*/React.createElement(Text, {
        weight: 600,
        display: "inline",
        color: colors.brand[400]
      }, readableData === null || readableData === void 0 ? void 0 : readableData.insurableItemIdentifier), ' ', "on a", ' ', /*#__PURE__*/React.createElement(Text, {
        weight: 600,
        display: "inline",
        color: colors.brand[400]
      }, readableData === null || readableData === void 0 ? void 0 : readableData.locIdentifier))
    }), /*#__PURE__*/React.createElement(Padding, {
      size: 24
    }, /*#__PURE__*/React.createElement(ScheduledLimitsAddForm, {
      lineOfCoverageType: lineOfCoverage.type,
      accountUuid: lineOfCoverage.accountUuid,
      lineOfCoverageUuid: lineOfCoverage.uuid,
      name: FORM_NAME$1,
      insurableItemOption: insurableItemOption,
      handleEdit: handleEdit
    })), /*#__PURE__*/React.createElement(ModalFooter, null, /*#__PURE__*/React.createElement(Flexbox, {
      justifyContent: "space-between",
      alignItems: "center",
      width: "100%"
    }, /*#__PURE__*/React.createElement(TextLink, {
      weight: 600,
      underline: false,
      onClick: async () => {
        setResetForm(true);
        setHasSubmitted(true);
        const isValid = await validateForm();

        if (!Object.keys(isValid).length) {
          await Promise.all([submitForm(), invalidateQuery(['scheduledLimits.getAvailableScheduleLimitsDefinitionsByLoc', {
            lineOfCoverageType: lineOfCoverage.type,
            lineOfCoverageUuid: lineOfCoverage.uuid,
            scheduledItemUuid: insurableItemOption === null || insurableItemOption === void 0 ? void 0 : insurableItemOption.scheduledItemUuid
          }]), invalidateQuery(['scheduledLimits.getAvailableInsurableItems'], {
            exact: false
          })]);
          resetForm();
          setHasSubmitted(false);
        }
      },
      disabled: isSubmitting
    }, "Save and add another"), /*#__PURE__*/React.createElement(Flexbox, {
      gap: 8,
      justifyContent: "flex-end"
    }, /*#__PURE__*/React.createElement(Button, {
      size: "secondary",
      type: "button",
      onClick: onToggle,
      loading: isSubmitting
    }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
      loading: isSubmitting,
      onClick: () => {
        setResetForm(false);
        setHasSubmitted(true);
      }
    }, "Create")))));
  }))), insurableItemsWithNoSchedule.length ? /*#__PURE__*/React.createElement(ScheduledItemsModal, {
    lineOfCoverage: lineOfCoverage,
    insurableItemsWithNoSchedule: insurableItemsWithNoSchedule,
    onToggle: () => setInsurableItemsWithNoSchedule([]),
    onSubmit: handleSubmitWithScheduledItems
  }) : null);
}

function getScheduledLimitFromFormValues(values, name) {
  return values[name].type ? {
    type: values[name].type,
    insurableItemType: values[name].insurableItemType,
    isLimitBlanketable: values[name].isLimitBlanketable
  } : undefined;
}

function useEditMultiSelectorData(data, initialInsurableItems) {
  // We need to reconcile the available items with the ones coming from editing
  const mergedInitialData = data.map(insurableItemOption => {
    const isInitialItem = initialInsurableItems.some(insurableItem => insurableItem.value === insurableItemOption.value);
    return { ...insurableItemOption,
      isDisabled: isInitialItem ? false : insurableItemOption.isDisabled,
      description: isInitialItem ? undefined : insurableItemOption.description
    };
  });
  const [filteredData, setFilteredData] = useState(mergedInitialData);

  const renderOptions = option => {
    return /*#__PURE__*/React.createElement(Padding, {
      y: 20
    }, /*#__PURE__*/React.createElement(Text, {
      weight: 600,
      color: option.isDisabled ? colors.steel[200] : 'primary'
    }, option.label), /*#__PURE__*/React.createElement(Text, {
      color: option.isDisabled ? colors.steel[200] : 'primary'
    }, option.description || option.readableType));
  };

  const renderSelected = option => {
    return /*#__PURE__*/React.createElement(Padding, {
      size: 20
    }, /*#__PURE__*/React.createElement(Text, {
      weight: 600
    }, option.label), /*#__PURE__*/React.createElement(Text, null, option.readableType));
  };

  const searchFunction = searchQuery => {
    if (!searchQuery) {
      setFilteredData(mergedInitialData);
      return;
    }

    const result = mergedInitialData.filter(item => item.label.toLowerCase().includes(searchQuery.toLowerCase()));
    setFilteredData(result);
  };

  const getRemoveIconType = option => {
    const isInInitialValues = initialInsurableItems.some(_ref => {
      let {
        value
      } = _ref;
      return option.value === value;
    });
    return isInInitialValues ? 'remove' : 'close';
  };

  return {
    filteredData,
    renderOptions,
    renderSelected,
    searchFunction,
    getRemoveIconType
  };
}

function DeleteFromBlanketModal(_ref) {
  let {
    scheduledLimitType,
    onToggle,
    lineOfCoverageType,
    insurableItemUuid,
    onDelete
  } = _ref;
  const {
    data,
    isLoading
  } = useScheduledLimitsReadableValues({
    scheduledLimitType,
    insurableItemUuid,
    lineOfCoverageType
  });

  const handleDelete = async () => {
    onDelete();
    return Promise.resolve();
  };

  return /*#__PURE__*/React.createElement(ActionModal, {
    variant: "dangerous",
    title: "Delete item from the  blanket scheduled coverage",
    isOpen: true,
    confirmText: "Delete",
    onClose: onToggle,
    onConfirm: () => handleDelete()
  }, isLoading && !data ? /*#__PURE__*/React.createElement(CircleSpinnerInline, {
    label: "Loading Schedule limit data..."
  }) : /*#__PURE__*/React.createElement(Text, null, "Are you sure you want delete", ' ', data !== null && data !== void 0 && data.scheduledLimitDisplayName ? /*#__PURE__*/React.createElement(Text, {
    weight: 600,
    display: "inline"
  }, data === null || data === void 0 ? void 0 : data.scheduledLimitDisplayName) : /*#__PURE__*/React.createElement(Text, {
    display: "inline"
  }, "this blanket limit"), data !== null && data !== void 0 && data.insurableItemIdentifier ? /*#__PURE__*/React.createElement(React.Fragment, null, ' ', "from", ' ', /*#__PURE__*/React.createElement(Text, {
    weight: 600,
    display: "inline"
  }, data === null || data === void 0 ? void 0 : data.insurableItemIdentifier)) : null, "?"));
}

function ScheduledLimitsEditForm(_ref) {
  var _errors$name;

  let {
    lineOfCoverage,
    name
  } = _ref;
  const {
    values,
    errors,
    setFieldValue,
    initialValues
  } = useFormikContext();
  const selectedScheduleLimit = getScheduledLimitFromFormValues(values, name);
  const insurableItemsError = getFormikErrorFromArray((_errors$name = errors[name]) === null || _errors$name === void 0 ? void 0 : _errors$name.insurableItems);
  const [itemToDeleteUuid, setItemToDeleteUuid] = useState('');
  const {
    useQuery
  } = useAdminTRPC();
  const {
    data
  } = useQuery(['scheduledLimits.getAvailableInsurableItems', {
    accountUuid: lineOfCoverage.accountUuid,
    lineOfCoverageUuid: lineOfCoverage.uuid,
    insurableItemType: selectedScheduleLimit === null || selectedScheduleLimit === void 0 ? void 0 : selectedScheduleLimit.insurableItemType,
    scheduledLimitType: selectedScheduleLimit === null || selectedScheduleLimit === void 0 ? void 0 : selectedScheduleLimit.type
  }], {
    enabled: !!selectedScheduleLimit
  });
  const initialData = data || [];
  const {
    filteredData,
    renderOptions,
    renderSelected,
    searchFunction,
    getRemoveIconType
  } = useEditMultiSelectorData(initialData, initialValues[name].insurableItems);
  return /*#__PURE__*/React.createElement(Stack, {
    gap: 24,
    direction: "vertical"
  }, values[name].isBlanket ? /*#__PURE__*/React.createElement(Field, {
    label: "Select scheduled items*",
    fieldId: `${name}.scheduleCoverageType`,
    error: insurableItemsError
  }, /*#__PURE__*/React.createElement(MultiObjectSelect, {
    renderOptions: renderOptions,
    renderSelected: renderSelected,
    searchFetch: searchFunction,
    showOptionsOnEmpty: true,
    data: filteredData,
    onSelectValue: values => {
      setFieldValue(`${name}.insurableItems`, Object.values(values));
    },
    onRemoveValue: value => {
      const {
        key
      } = value;
      const isInitialItem = initialValues[name].insurableItems.some(insurableItem => insurableItem.value === key);

      if (isInitialItem) {
        setItemToDeleteUuid(key);
        return;
      }

      const items = values[name].insurableItems.filter(item => item.value !== key);
      setFieldValue(`${name}.insurableItems`, items);
    },
    selectedItems: toRecord(values[name].insurableItems || [], insurableItem => insurableItem.value),
    formatter: result => {
      return {
        title: result.label,
        key: result.value
      };
    },
    getRemoveIconType: getRemoveIconType
  })) : null, selectedScheduleLimit && /*#__PURE__*/React.createElement(ScheduleLimitDslForm, {
    insurableItemType: selectedScheduleLimit.insurableItemType,
    lineOfCoverageType: lineOfCoverage.type,
    scheduledLimitType: selectedScheduleLimit.type,
    name: name
  }), values[name].insurableItems.length && selectedScheduleLimit ? /*#__PURE__*/React.createElement(Suspense, {
    fallback: /*#__PURE__*/React.createElement(CircleSpinnerInline, {
      label: "Loading insurable values..."
    })
  }, /*#__PURE__*/React.createElement(InsurableValuesSelectList, {
    name: name,
    accountUuid: lineOfCoverage.accountUuid,
    insurableItemType: selectedScheduleLimit.insurableItemType,
    insurableItemUuids: values[name].insurableItems.map(insurableItem => insurableItem.value)
  })) : null, itemToDeleteUuid ? /*#__PURE__*/React.createElement(DeleteFromBlanketModal, {
    lineOfCoverageType: lineOfCoverage.type,
    insurableItemUuid: itemToDeleteUuid,
    scheduledLimitType: selectedScheduleLimit === null || selectedScheduleLimit === void 0 ? void 0 : selectedScheduleLimit.type,
    onToggle: () => setItemToDeleteUuid(''),
    onDelete: () => {
      const items = values[name].insurableItems.filter(item => item.value !== itemToDeleteUuid);
      setFieldValue(`${name}.insurableItems`, items);
    }
  }) : null);
}

function getValidationSchema(name) {
  return yup.object({
    [name]: yup.object({
      lineOfCoverageType: yup.string().required('At least one certificate holder name is required'),
      type: yup.string().required('Please select a Scheduled coverage'),
      insurableValuesSnapshot: yup.array().min(0, 'Please select at least one Insurable value')
    })
  });
}

const FORM_NAME = 'form';
function ScheduledLimitsEditModal(_ref) {
  let {
    scheduledLimit,
    onToggle,
    lineOfCoverage,
    isInsurableItemContext = false
  } = _ref;
  const notifications = useNotifications();
  const [insurableItemsWithNoSchedule, setInsurableItemsWithNoSchedule] = useState([]);
  const [initialFormValues, setInitialFormValues] = useState({});
  const {
    useMutation,
    invalidateQuery,
    useQuery
  } = useAdminTRPC();
  const isBlanket = !scheduledLimit.scheduledItemUuid || !!scheduledLimit.data.blanket;
  const {
    data,
    isLoading
  } = useQuery(['scheduledLimits.getScheduledLimitToEdit', {
    scheduledLimitUuid: scheduledLimit.uuid,
    lineOfCoverageUuid: lineOfCoverage.uuid,
    scheduledLimitType: scheduledLimit.type,
    isBlanket,
    lineOfCoverageType: lineOfCoverage.type
  }]);
  const {
    data: readableData,
    isLoading: isLoadingReadable
  } = useScheduledLimitsReadableValues({
    scheduledLimitType: scheduledLimit.type,
    insurableItemUuid: scheduledLimit.insurableItemUuid,
    lineOfCoverageType: lineOfCoverage.type
  });
  const {
    mutateAsync: replaceScheduledLimit
  } = useMutation('scheduledLimits.replaceScheduledLimit', {
    onSuccess: async () => {
      await invalidateQuery([isInsurableItemContext ? 'scheduledLimits.getScheduledLimitsDataForII' : 'scheduledLimits.getScheduledLimitsDataAndMetadata'], {
        exact: false
      });
      onToggle();
      notifications.add({
        title: 'Scheduled limit succesfully updated...',
        type: 'success'
      });
    }
  });

  const handleSubmitWithScheduledItems = async values => {
    const form = { ...initialFormValues[FORM_NAME],
      ...values
    };
    await replaceScheduledLimit(form);
  };

  const message = isBlanket ? /*#__PURE__*/React.createElement(Text, {
    color: colors.brand[400]
  }, "You\u2019re editing", ' ', /*#__PURE__*/React.createElement(Text, {
    weight: 600,
    display: "inline",
    color: colors.brand[400]
  }, "blanket ", readableData === null || readableData === void 0 ? void 0 : readableData.scheduledLimitDisplayName), ' ', "coverage.") : /*#__PURE__*/React.createElement(Text, {
    color: colors.brand[400]
  }, "You are editing", ' ', /*#__PURE__*/React.createElement(Text, {
    weight: 600,
    display: "inline",
    color: colors.brand[400]
  }, readableData === null || readableData === void 0 ? void 0 : readableData.scheduledLimitDisplayName), ' ', "scheduled limit for the", ' ', /*#__PURE__*/React.createElement(Text, {
    weight: 600,
    display: "inline",
    color: colors.brand[400]
  }, readableData === null || readableData === void 0 ? void 0 : readableData.insurableItemIdentifier));
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Modal, {
    isOpen: true,
    onToggle: onToggle
  }, /*#__PURE__*/React.createElement(ModalBody, null, /*#__PURE__*/React.createElement(ModalHeader, {
    titleText: "Edit scheduled coverage",
    onClose: onToggle
  }), isLoading || !data || isLoadingReadable || !readableData ? /*#__PURE__*/React.createElement(Flexbox, {
    height: 300,
    width: "100%",
    justifyContent: "center",
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(CircleSpinnerInline, {
    label: "Loading Schedule limit data..."
  })) : /*#__PURE__*/React.createElement(Formik, {
    initialValues: {
      [FORM_NAME]: {
        uuid: data.uuid,
        startDate: data.startDate.toString(),
        endDate: data.endDate.toString(),
        insurableItems: data.insurableItems,
        insurableItemType: data.insurableItemType,
        lineOfCoverageType: data.lineOfCoverageType,
        lineOfCoverageUuid: data.lineOfCoverageUuid,
        insurableValuesSnapshot: data.insurableValuesSnapshot,
        version: data.version,
        data: data.data,
        type: data.type,
        isLimitBlanketable: false,
        isBlanket
      }
    },
    validationSchema: getValidationSchema(FORM_NAME),
    onSubmit: async values => {
      const filteredInsurableItems = values[FORM_NAME].insurableItems.filter(insurableItem => !insurableItem.scheduledItemUuid);

      if (!filteredInsurableItems.length) {
        // If no orphan insurable items, replace the scheduled limit
        await replaceScheduledLimit(values[FORM_NAME]);
      } else {
        // otherwise, store the original form data and show the add scheduled items modal
        setInitialFormValues(values);
        setInsurableItemsWithNoSchedule(filteredInsurableItems);
      }
    }
  }, _ref2 => {
    let {
      handleSubmit,
      isSubmitting
    } = _ref2;
    return /*#__PURE__*/React.createElement("form", {
      onSubmit: handleSubmit
    }, /*#__PURE__*/React.createElement(MessageBox, {
      borderless: true,
      variant: "neutral",
      message: message
    }), /*#__PURE__*/React.createElement(Padding, {
      size: 24
    }, /*#__PURE__*/React.createElement(ScheduledLimitsEditForm, {
      lineOfCoverage: lineOfCoverage,
      name: FORM_NAME
    })), /*#__PURE__*/React.createElement(ModalFooter, null, /*#__PURE__*/React.createElement(Flexbox, {
      gap: 8,
      justifyContent: "flex-end"
    }, /*#__PURE__*/React.createElement(Button, {
      size: "secondary",
      type: "button",
      onClick: onToggle
    }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
      loading: isSubmitting
    }, "Save"))));
  }))), insurableItemsWithNoSchedule.length ? /*#__PURE__*/React.createElement(ScheduledItemsModal, {
    lineOfCoverage: lineOfCoverage,
    insurableItemsWithNoSchedule: insurableItemsWithNoSchedule,
    onToggle: () => setInsurableItemsWithNoSchedule([]),
    onSubmit: handleSubmitWithScheduledItems
  }) : null);
}

const ScheduledLimitsPermissionsProvider = createProvider(props => props);

function useScheduledLimitsPermissions() {
  const {
    canManage
  } = useProvider(ScheduledLimitsPermissionsProvider);
  return {
    canManage
  };
}

function useInsurableItemLimitsAdditionalColumns(_ref) {
  let {
    handleDelete,
    handleEdit,
    schema,
    insurableItemsMetadata
  } = _ref;
  const {
    canManage
  } = useScheduledLimitsPermissions();
  return useMemo(() => {
    const columns = [{
      column: {
        Cell: _ref2 => {
          let {
            row
          } = _ref2;
          return /*#__PURE__*/React.createElement("div", null, row.original.label);
        },
        accessor: 'data',
        id: 'main',
        Header: 'Scheduled limit',
        width: 290
      },
      position: 0
    }];

    if (canManage) {
      var _schema$spreadsheet;

      columns.push({
        column: {
          Cell: _ref3 => {
            let {
              row
            } = _ref3;
            return /*#__PURE__*/React.createElement(Flexbox, {
              width: "100%",
              justifyContent: "flex-end"
            }, /*#__PURE__*/React.createElement(Tooltip, {
              text: "Edit"
            }, /*#__PURE__*/React.createElement(StyledActionButton$5, {
              onClick: () => handleEdit(row.original)
            }, /*#__PURE__*/React.createElement(IconPencil, {
              color: colors.steel[400]
            }))), /*#__PURE__*/React.createElement(Tooltip, {
              text: "Remove"
            }, /*#__PURE__*/React.createElement(StyledActionButton$5, {
              onClick: () => handleDelete(row.original)
            }, /*#__PURE__*/React.createElement(IconRemove, {
              color: colors.steel[400],
              size: 12
            }))));
          },
          accessor: 'data',
          id: 'actions',
          Header: '',
          disableSortBy: true,
          focusable: false,
          width: 80,
          maxWidth: 80,
          sticky: 'right',
          disableResizing: true
        },
        position: (((_schema$spreadsheet = schema.spreadsheet) === null || _schema$spreadsheet === void 0 ? void 0 : _schema$spreadsheet.columns.length) || 0) + 1
      });
    }

    return columns;
  }, [handleEdit, handleDelete, schema, canManage, insurableItemsMetadata]);
}
const StyledActionButton$5 = styled.button`
  align-items: center;
  background: #fff;
  border: none;
  border-radius: 50%;
  display: flex;
  height: 32px;
  justify-content: center;
  width: 32px;
  background: none;
  pointer-events: initial;
  transition: background 0.3s ease;
  cursor: pointer;

  &:hover {
    background: ${colors.steel[100]};
  }
`;

function InsurableItemsLimitsSpreadsheet(_ref) {
  let {
    insurableItemsScheduledLimits,
    schema,
    handleEdit,
    handleDelete,
    insurableItemsMetadata
  } = _ref;
  const scheduledItemsAdditionalColumns = useInsurableItemLimitsAdditionalColumns({
    handleDelete,
    handleEdit,
    schema,
    insurableItemsMetadata
  });
  return /*#__PURE__*/React.createElement(DSLSpreadsheet, {
    tableId: "scheduledItems",
    data: insurableItemsScheduledLimits,
    schema: schema,
    expandable: true,
    hideExpandColumn: true,
    rowHeight: 48,
    additionalColumns: scheduledItemsAdditionalColumns,
    styleOptions: {
      borderTop: false,
      rowBackground: colors.steel[50]
    }
  });
}

function ScheduledLimitsForIISpreadsheet(_ref) {
  let {
    insurableItemsScheduledLimits = [],
    schema,
    insurableItemsMetadata,
    lineOfCoverage,
    emptyText = 'There is no coverage yet',
    insurableItemOption
  } = _ref;
  const [modalVisible, toggleModalVisible] = useToggle(false);
  const [selectedItemToDelete, setSelectedItemToDelete] = useState(null);
  const [scheduledLimitUuidToEdit, setScheduledLimitUuidToEdit] = useState(null);
  const {
    canManage
  } = useScheduledLimitsPermissions();
  const handleEdit = useCallback(data => {
    toggleModalVisible(false);
    setScheduledLimitUuidToEdit(data);
  }, [setScheduledLimitUuidToEdit, toggleModalVisible]);
  const handleDelete = useCallback(data => {
    setSelectedItemToDelete(data);
  }, [setSelectedItemToDelete]);

  function onAdd() {
    toggleModalVisible(true);
  }

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ExpandableModule, {
    defaultExpanded: true,
    label: "Scheduled coverage for this item",
    onAdd: canManage ? onAdd : undefined
  }, insurableItemsScheduledLimits.length !== 0 ? /*#__PURE__*/React.createElement(InsurableItemsLimitsSpreadsheet, {
    schema: schema,
    handleDelete: handleDelete,
    handleEdit: handleEdit,
    insurableItemsScheduledLimits: insurableItemsScheduledLimits,
    insurableItemsMetadata: insurableItemsMetadata
  }) : /*#__PURE__*/React.createElement(Padding, {
    size: 20
  }, /*#__PURE__*/React.createElement(Flexbox, {
    justifyContent: "center",
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Text, null, emptyText), canManage && /*#__PURE__*/React.createElement(TextLink, {
    underline: false,
    underlineOnHover: true,
    onClick: canManage ? onAdd : undefined,
    style: {
      paddingLeft: 5
    }
  }, "add coverage")))), modalVisible && /*#__PURE__*/React.createElement(ScheduledLimitsAddModal, {
    lineOfCoverage: lineOfCoverage,
    onToggle: toggleModalVisible,
    insurableItemOption: insurableItemOption,
    handleEdit: handleEdit
  }), scheduledLimitUuidToEdit && /*#__PURE__*/React.createElement(ScheduledLimitsEditModal, {
    onToggle: () => setScheduledLimitUuidToEdit(null),
    scheduledLimit: scheduledLimitUuidToEdit,
    lineOfCoverage: lineOfCoverage,
    isInsurableItemContext: true
  }), selectedItemToDelete && /*#__PURE__*/React.createElement(ScheduledLimitDeleteModal, {
    scheduledLimit: selectedItemToDelete,
    onToggle: () => setSelectedItemToDelete(null),
    lineOfCoverageType: lineOfCoverage.type,
    isInsurableItemContext: true
  }));
}

function useBlanketLimitsAdditionalColumns(_ref) {
  let {
    handleDelete,
    handleEdit,
    schema
  } = _ref;
  const {
    canManage
  } = useScheduledLimitsPermissions();
  return useMemo(() => {
    const columns = [{
      column: {
        Cell: _ref2 => {
          let {
            row
          } = _ref2;
          return /*#__PURE__*/React.createElement("div", null, row.original.readableType);
        },
        accessor: 'data',
        id: 'main',
        Header: 'Scheduled coverage',
        width: 290
      },
      position: 0
    }];

    if (canManage) {
      var _schema$spreadsheet;

      columns.push({
        column: {
          Cell: _ref3 => {
            let {
              row
            } = _ref3;
            return /*#__PURE__*/React.createElement(Flexbox, {
              width: "100%",
              justifyContent: "flex-end"
            }, /*#__PURE__*/React.createElement(Tooltip, {
              text: "Edit"
            }, /*#__PURE__*/React.createElement(StyledActionButton$4, {
              onClick: () => handleEdit(row.original)
            }, /*#__PURE__*/React.createElement(IconPencil, {
              color: colors.steel[400]
            }))), /*#__PURE__*/React.createElement(Tooltip, {
              text: "Remove"
            }, /*#__PURE__*/React.createElement(StyledActionButton$4, {
              onClick: () => handleDelete(row.original)
            }, /*#__PURE__*/React.createElement(IconRemove, {
              color: colors.steel[400],
              size: 12
            }))));
          },
          accessor: 'data',
          id: 'actions',
          Header: '',
          disableSortBy: true,
          focusable: false,
          width: 80,
          maxWidth: 80
        },
        position: (((_schema$spreadsheet = schema.spreadsheet) === null || _schema$spreadsheet === void 0 ? void 0 : _schema$spreadsheet.columns.length) || 0) + 1
      });
    }

    return columns;
  }, [handleEdit, handleDelete, schema, canManage]);
}
const StyledActionButton$4 = styled.button`
  align-items: center;
  background: #fff;
  border: none;
  border-radius: 50%;
  display: flex;
  height: 32px;
  justify-content: center;
  width: 32px;
  background: none;
  pointer-events: initial;
  transition: background 0.3s ease;
  cursor: pointer;

  &:hover {
    background: ${colors.steel[100]};
  }
`;

function BlanketLimitsSpreadsheet(_ref) {
  let {
    blanketLimits,
    schema,
    handleEdit,
    handleDelete
  } = _ref;
  const blanketAdditionalColumns = useBlanketLimitsAdditionalColumns({
    handleDelete,
    handleEdit,
    schema
  });
  return /*#__PURE__*/React.createElement(DSLSpreadsheet, {
    tableId: "blaket",
    data: blanketLimits,
    schema: schema,
    expandable: true,
    rowHeight: 48,
    additionalColumns: blanketAdditionalColumns,
    styleOptions: {
      borderTop: false,
      rowBackground: colors.steel[50]
    }
  });
}

let ScheduledLimitsGrouping;

(function (ScheduledLimitsGrouping) {
  ScheduledLimitsGrouping["BY_ITEM"] = "BY_ITEM";
  ScheduledLimitsGrouping["BY_COVERAGE"] = "BY_COVERAGE";
})(ScheduledLimitsGrouping || (ScheduledLimitsGrouping = {}));

function useScheduleLimitsAdditionalColumns(_ref) {
  let {
    handleDelete,
    handleEdit,
    schema,
    insurableItemsMetadata,
    scheduledCoverageInsurableItemType
  } = _ref;
  const {
    canManage
  } = useScheduledLimitsPermissions();
  const [insurableItemType] = scheduledCoverageInsurableItemType;
  return useMemo(() => {
    var _insurableItemsMetada, _insurableItemsMetada2;

    const columns = [{
      column: {
        Cell: _ref2 => {
          let {
            row
          } = _ref2;
          return /*#__PURE__*/React.createElement("div", null, row.canExpand ? row.original.data.type : row.original.subRowLabel);
        },
        accessor: 'data',
        id: 'main',
        Header: scheduledCoverageInsurableItemType.length === 1 ? pluralize((_insurableItemsMetada = insurableItemsMetadata.find(item => item.definition.id === insurableItemType)) === null || _insurableItemsMetada === void 0 ? void 0 : (_insurableItemsMetada2 = _insurableItemsMetada.definition) === null || _insurableItemsMetada2 === void 0 ? void 0 : _insurableItemsMetada2.readableName, 2) : 'Scheduled coverage',
        width: 290
      },
      position: 0
    }];

    if (canManage) {
      var _schema$spreadsheet;

      columns.push({
        column: {
          Cell: _ref3 => {
            let {
              row
            } = _ref3;
            return /*#__PURE__*/React.createElement(React.Fragment, null, row.canExpand ? '' : /*#__PURE__*/React.createElement(Flexbox, {
              width: "100%",
              justifyContent: "flex-end"
            }, /*#__PURE__*/React.createElement(Tooltip, {
              text: "Edit"
            }, /*#__PURE__*/React.createElement(StyledActionButton$3, {
              onClick: () => handleEdit(row.original)
            }, /*#__PURE__*/React.createElement(IconPencil, {
              color: colors.steel[400]
            }))), /*#__PURE__*/React.createElement(Tooltip, {
              text: "Remove"
            }, /*#__PURE__*/React.createElement(StyledActionButton$3, {
              onClick: () => handleDelete(row.original)
            }, /*#__PURE__*/React.createElement(IconRemove, {
              color: colors.steel[400],
              size: 12
            })))));
          },
          accessor: 'data',
          id: 'actions',
          Header: '',
          disableSortBy: true,
          focusable: false,
          width: 80,
          maxWidth: 80
        },
        position: (((_schema$spreadsheet = schema.spreadsheet) === null || _schema$spreadsheet === void 0 ? void 0 : _schema$spreadsheet.columns.length) || 0) + 1
      });
    }

    return columns;
  }, [handleEdit, handleDelete, schema, canManage]);
}
const StyledActionButton$3 = styled.button`
  align-items: center;
  background: #fff;
  border: none;
  border-radius: 50%;
  display: flex;
  height: 32px;
  justify-content: center;
  width: 32px;
  background: none;
  pointer-events: initial;
  transition: background 0.3s ease;
  cursor: pointer;

  &:hover {
    background: ${colors.steel[100]};
  }
`;

function ScheduledCoverageSpreadsheet(_ref) {
  let {
    scheduledLimitsByItem,
    scheduledLimitsByCoverage,
    schema,
    handleEdit,
    handleDelete,
    selectedGroupBy,
    scheduledCoverageInsurableItemType,
    insurableItemsMetadata
  } = _ref;
  const scheduledCoverageAdditionalColumns = useScheduleLimitsAdditionalColumns({
    handleDelete,
    handleEdit,
    schema,
    scheduledCoverageInsurableItemType,
    insurableItemsMetadata
  });
  return /*#__PURE__*/React.createElement(DSLSpreadsheet, {
    tableId: "coverage",
    data: selectedGroupBy === ScheduledLimitsGrouping.BY_ITEM ? scheduledLimitsByItem : scheduledLimitsByCoverage,
    schema: schema,
    expandable: true,
    expandSubrows: true,
    rowHeight: 48,
    additionalColumns: scheduledCoverageAdditionalColumns,
    overrideFieldRender: {
      limit: _ref2 => {
        let {
          row
        } = _ref2;
        return row.data.blanket ? /*#__PURE__*/React.createElement(Text, null, "Included in blanket") : null;
      }
    },
    styleOptions: {
      borderTop: false
    }
  });
}

function useScheduleItemsForLimitsAdditionalColumns(_ref) {
  let {
    handleDelete,
    handleEdit,
    schema,
    insurableItemsMetadata,
    scheduledItemsInsurableItemType
  } = _ref;
  const {
    canManage
  } = useScheduledLimitsPermissions();
  const [insurableItemType] = scheduledItemsInsurableItemType;
  return useMemo(() => {
    var _insurableItemsMetada, _insurableItemsMetada2;

    const columns = [{
      column: {
        Cell: _ref2 => {
          let {
            row
          } = _ref2;
          return /*#__PURE__*/React.createElement("div", null, row.original.label);
        },
        accessor: 'data',
        id: 'main',
        Header: scheduledItemsInsurableItemType.length === 1 ? pluralize((_insurableItemsMetada = insurableItemsMetadata.find(item => item.definition.id === insurableItemType)) === null || _insurableItemsMetada === void 0 ? void 0 : (_insurableItemsMetada2 = _insurableItemsMetada.definition) === null || _insurableItemsMetada2 === void 0 ? void 0 : _insurableItemsMetada2.readableName, 2) : 'Scheduled items',
        width: 290
      },
      position: 0
    }];

    if (canManage) {
      var _schema$spreadsheet;

      columns.push({
        column: {
          Cell: _ref3 => {
            let {
              row
            } = _ref3;
            return /*#__PURE__*/React.createElement(Flexbox, {
              width: "100%",
              justifyContent: "flex-end"
            }, /*#__PURE__*/React.createElement(Tooltip, {
              text: "Edit"
            }, /*#__PURE__*/React.createElement(StyledActionButton$2, {
              onClick: () => handleEdit(row.original)
            }, /*#__PURE__*/React.createElement(IconPencil, {
              color: colors.steel[400]
            }))), /*#__PURE__*/React.createElement(Tooltip, {
              text: "Remove"
            }, /*#__PURE__*/React.createElement(StyledActionButton$2, {
              onClick: () => handleDelete(row.original)
            }, /*#__PURE__*/React.createElement(IconRemove, {
              color: colors.steel[400],
              size: 12
            }))));
          },
          accessor: 'data',
          id: 'actions',
          Header: '',
          disableSortBy: true,
          focusable: false,
          width: 80,
          maxWidth: 80
        },
        position: (((_schema$spreadsheet = schema.spreadsheet) === null || _schema$spreadsheet === void 0 ? void 0 : _schema$spreadsheet.columns.length) || 0) + 1
      });
    }

    return columns;
  }, [handleEdit, handleDelete, schema, canManage, insurableItemsMetadata]);
}
const StyledActionButton$2 = styled.button`
  align-items: center;
  background: #fff;
  border: none;
  border-radius: 50%;
  display: flex;
  height: 32px;
  justify-content: center;
  width: 32px;
  background: none;
  pointer-events: initial;
  transition: background 0.3s ease;
  cursor: pointer;

  &:hover {
    background: ${colors.steel[100]};
  }
`;

function ScheduledItemsLimitsSpreadsheet(_ref) {
  let {
    scheduledItems,
    schema,
    handleEdit,
    handleDelete,
    insurableItemsMetadata,
    scheduledItemsInsurableItemType
  } = _ref;
  const scheduledItemsAdditionalColumns = useScheduleItemsForLimitsAdditionalColumns({
    handleDelete,
    handleEdit,
    schema,
    insurableItemsMetadata,
    scheduledItemsInsurableItemType
  });
  return /*#__PURE__*/React.createElement(DSLSpreadsheet, {
    tableId: "scheduledItems",
    data: scheduledItems,
    schema: schema,
    expandable: true,
    rowHeight: 48,
    additionalColumns: scheduledItemsAdditionalColumns,
    styleOptions: {
      borderTop: false,
      rowBackground: colors.steel[50]
    }
  });
}

function ScheduledLimitsSpreadsheet(_ref) {
  let {
    scheduledLimitsByItem = [],
    scheduledLimitsByCoverage = [],
    scheduledItems = [],
    blanketLimits = [],
    schema,
    scheduledItemsSchema,
    scheduledCoverageSchema,
    insurableItemsMetadata,
    scheduledCoverageInsurableItemType,
    scheduledItemsInsurableItemType,
    lineOfCoverage,
    emptyText = 'There is no coverage yet'
  } = _ref;
  const [modalVisible, toggleModalVisible] = useToggle(false);
  const [selectedItemToDelete, setSelectedItemToDelete] = useState(null);
  const [scheduledLimitUuidToEdit, setScheduledLimitUuidToEdit] = useState(null);
  const [selectedGroupBy, setSelectedGroupBy] = useState(ScheduledLimitsGrouping.BY_ITEM);
  const {
    canManage
  } = useScheduledLimitsPermissions();
  const handleEdit = useCallback(data => {
    toggleModalVisible(false);
    setScheduledLimitUuidToEdit(data);
  }, [setScheduledLimitUuidToEdit, toggleModalVisible]);
  const handleDelete = useCallback(data => {
    setSelectedItemToDelete(data);
  }, [setSelectedItemToDelete]);
  const options = [{
    label: 'By Item',
    value: ScheduledLimitsGrouping.BY_ITEM
  }, {
    label: 'By Coverage',
    value: ScheduledLimitsGrouping.BY_COVERAGE
  }];

  function onAdd() {
    toggleModalVisible(true);
  }

  return /*#__PURE__*/React.createElement(React.Fragment, null, blanketLimits.length !== 0 ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ExpandableModule, {
    defaultExpanded: true,
    label: "Blanket coverages",
    onAdd: canManage ? onAdd : undefined
  }, /*#__PURE__*/React.createElement(BlanketLimitsSpreadsheet, {
    schema: schema,
    handleDelete: handleDelete,
    handleEdit: handleEdit,
    blanketLimits: blanketLimits
  })), /*#__PURE__*/React.createElement(Spacing, {
    height: 24
  })) : null, /*#__PURE__*/React.createElement(ExpandableModule, {
    defaultExpanded: true,
    label: "Scheduled coverages",
    withSegmentedControl: {
      options,
      onChange: setSelectedGroupBy,
      value: selectedGroupBy
    },
    onAdd: canManage ? onAdd : undefined
  }, scheduledLimitsByItem.length !== 0 ? /*#__PURE__*/React.createElement(ScheduledCoverageSpreadsheet, {
    schema: scheduledCoverageSchema,
    handleDelete: handleDelete,
    handleEdit: handleEdit,
    selectedGroupBy: selectedGroupBy,
    scheduledLimitsByItem: scheduledLimitsByItem,
    scheduledLimitsByCoverage: scheduledLimitsByCoverage,
    insurableItemsMetadata: insurableItemsMetadata,
    scheduledCoverageInsurableItemType: scheduledCoverageInsurableItemType
  }) : /*#__PURE__*/React.createElement(Padding, {
    size: 20
  }, /*#__PURE__*/React.createElement(Flexbox, {
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Text, null, emptyText), canManage && /*#__PURE__*/React.createElement(TextLink, {
    underline: false,
    underlineOnHover: true,
    onClick: onAdd,
    style: {
      paddingLeft: 5
    }
  }, "add coverage")))), scheduledItems.length ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Spacing, {
    height: 32
  }), /*#__PURE__*/React.createElement(ExpandableModule, {
    defaultExpanded: true,
    label: "Scheduled items",
    onAdd: canManage ? onAdd : undefined
  }, scheduledItems.length !== 0 ? /*#__PURE__*/React.createElement(ScheduledItemsLimitsSpreadsheet, {
    schema: scheduledItemsSchema,
    handleDelete: handleDelete,
    handleEdit: handleEdit,
    scheduledItems: scheduledItems,
    insurableItemsMetadata: insurableItemsMetadata,
    scheduledItemsInsurableItemType: scheduledItemsInsurableItemType
  }) : /*#__PURE__*/React.createElement(Padding, {
    size: 20
  }, /*#__PURE__*/React.createElement(Flexbox, {
    justifyContent: "center",
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Text, null, emptyText), canManage && /*#__PURE__*/React.createElement(TextLink, {
    underline: false,
    underlineOnHover: true,
    onClick: canManage ? onAdd : undefined,
    style: {
      paddingLeft: 5
    }
  }, "add coverage"))))) : null, modalVisible && /*#__PURE__*/React.createElement(ScheduledLimitsAddModal, {
    lineOfCoverage: lineOfCoverage,
    onToggle: toggleModalVisible,
    handleEdit: handleEdit
  }), scheduledLimitUuidToEdit && /*#__PURE__*/React.createElement(ScheduledLimitsEditModal, {
    onToggle: () => setScheduledLimitUuidToEdit(null),
    scheduledLimit: scheduledLimitUuidToEdit,
    lineOfCoverage: lineOfCoverage
  }), selectedItemToDelete && /*#__PURE__*/React.createElement(ScheduledLimitDeleteModal, {
    scheduledLimit: selectedItemToDelete,
    onToggle: () => setSelectedItemToDelete(null),
    lineOfCoverageType: lineOfCoverage.type
  }));
}

function LineOfCoverageScheduledLimits(_ref) {
  let {
    lineOfCoverage,
    canManage
  } = _ref;
  return /*#__PURE__*/React.createElement(Suspense, {
    fallback: /*#__PURE__*/React.createElement(Flexbox, {
      justifyContent: "flex-start",
      alignItems: "center"
    }, /*#__PURE__*/React.createElement(CircleSpinnerInline, null))
  }, /*#__PURE__*/React.createElement(LineOfCoverageScheduledLimitsInner, {
    lineOfCoverage: lineOfCoverage,
    canManage: canManage
  }));
}

function LineOfCoverageScheduledLimitsInner(_ref2) {
  let {
    lineOfCoverage,
    canManage
  } = _ref2;
  const {
    type,
    uuid,
    accountUuid
  } = lineOfCoverage;
  const {
    data
  } = useScheduledLimitsMetadata({
    lineOfCoverageType: type,
    lineOfCoverageUuid: uuid,
    accountUuid
  }, {
    suspense: true
  });

  if (!data) {
    return null;
  }

  return /*#__PURE__*/React.createElement(ScheduledLimitsPermissionsProvider, {
    canManage: canManage
  }, /*#__PURE__*/React.createElement(ScheduledLimitsSpreadsheet, {
    scheduledLimitsByItem: data.scheduleLimitMetadataResponse.scheduledCoveragesByItem,
    scheduledLimitsByCoverage: data.scheduleLimitMetadataResponse.scheduledCoveragesByCoverage,
    scheduledItems: data.scheduleLimitMetadataResponse.scheduledItems,
    blanketLimits: data.scheduleLimitMetadataResponse.blanketLimits,
    schema: data.scheduleLimitMetadataResponse.metadata.schema,
    scheduledItemsSchema: data.scheduleLimitMetadataResponse.metadata.scheduledItemsSchema,
    scheduledCoverageSchema: data.scheduleLimitMetadataResponse.metadata.scheduledCoverageSchema,
    scheduledCoverageInsurableItemType: data.scheduleLimitMetadataResponse.metadata.scheduledCoverageInsurableItemType,
    scheduledItemsInsurableItemType: data.scheduleLimitMetadataResponse.metadata.scheduledItemsInsurableItemType,
    insurableItemsMetadata: data.scheduleLimitMetadataResponse.metadata.insurableItemsMetadata,
    lineOfCoverage: lineOfCoverage
  }));
}

function LineOfCoverageDisplayInner(_ref) {
  var _lineOfCoverage$start, _lineOfCoverage$endDa;

  let {
    lineOfCoverage,
    isLineageFeatureFlagOn,
    canManage = false,
    getLineOfCoverageDetailsLink,
    getPolicyDetailsLink
  } = _ref;
  return /*#__PURE__*/React.createElement(React.Fragment, null, lineOfCoverage.isScheduledLimitSupported ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(LineOfCoverageScheduledLimits, {
    lineOfCoverage: { ...lineOfCoverage,
      startDate: (_lineOfCoverage$start = lineOfCoverage.startDate) === null || _lineOfCoverage$start === void 0 ? void 0 : _lineOfCoverage$start.toDate('UTC').toISOString(),
      endDate: (_lineOfCoverage$endDa = lineOfCoverage.endDate) === null || _lineOfCoverage$endDa === void 0 ? void 0 : _lineOfCoverage$endDa.toDate('UTC').toISOString()
    },
    canManage: canManage
  }), /*#__PURE__*/React.createElement(Spacing, {
    height: 24
  })) : null, /*#__PURE__*/React.createElement(ExpandableModule, {
    label: "Other coverage",
    defaultExpanded: true
  }, /*#__PURE__*/React.createElement(LineOfCoverageDataDisplay, {
    lineOfCoverage: lineOfCoverage
  })), isLineageFeatureFlagOn && /*#__PURE__*/React.createElement(LineOfCoverageLineage, {
    lineOfCoverage: lineOfCoverage,
    getLineOfCoverageDetailsLink: getLineOfCoverageDetailsLink,
    getPolicyDetailsLink: getPolicyDetailsLink
  }));
}

function useEnrichedLocByUuid(params, options) {
  const {
    useQuery
  } = useAdminTRPC();
  return useQuery(['loc.getByUuid', params], options);
}

const POLICY_STATUS_LABEL_MAP = {
  [PolicyStatus.ACTIVE]: 'Active',
  [PolicyStatus.EXPIRED]: 'Expired',
  [PolicyStatus.CANCELLED]: 'Cancelled',
  [PolicyStatus.UPCOMING]: 'Upcoming'
};

function LineOfCoverageDataInput(_ref) {
  let {
    name,
    lineOfCoverageType,
    lineOfCoverageVersion
  } = _ref;
  const {
    data
  } = useLineOfCoverageMetadata({
    lineOfCoverageType,
    lineOfCoverageVersion
  }, {
    suspense: true
  });
  const {
    values,
    errors,
    setFieldValue
  } = useFormikContext(); // make sure we always have at least an empty object in "data"

  useEffect(() => {
    const formValuesData = get(values, `${name}.data`);

    if (!formValuesData) {
      setFieldValue(`${name}.data`, {});
    }
  }, [setFieldValue, name, values]); // set "type" and keep it updated

  useEffect(() => {
    setFieldValue(`${name}.type`, lineOfCoverageType);
  }, [setFieldValue, name, lineOfCoverageType]); // set "version" and keep it updated

  useEffect(() => {
    if (!data) return;
    const formValuesVersion = get(values, `${name}.version`);

    if (isNil(formValuesVersion) && isNil(lineOfCoverageVersion)) {
      // use the fetched "currentVersion" if no version is set in the form and no explicit version was passed to the component
      setFieldValue(`${name}.version`, data.definition.currentVersion);
    } else if (isNotNil(lineOfCoverageVersion) && lineOfCoverageVersion !== formValuesVersion) {
      // use the explicit version passed to the component if no version is set in the form
      setFieldValue(`${name}.version`, lineOfCoverageVersion);
    }
  }, [lineOfCoverageVersion, values, setFieldValue, data, name]); // manage "displayName" changes

  const handleDisplayNameChange = value => {
    setFieldValue(`${name}.displayName`, value);
  };

  const claimsMadeOrOccurrence = `${name}.globalFields.claimsMadeOrOccurrence`;
  const coverageTerritory = `${name}.globalFields.coverageTerritory`;
  const auditable = `${name}.globalFields.auditable`;
  const terrorismRiskInsuranceAct = `${name}.globalFields.terrorismRiskInsuranceAct`;
  const additionalDetails = `${name}.globalFields.additionalDetails`; // return the input to add information into the "data" object, which must follow the structure determined by the schema

  return /*#__PURE__*/React.createElement(Stack, {
    gap: 32,
    direction: "vertical"
  }, /*#__PURE__*/React.createElement(Field, {
    key: `${name}.displayName`,
    fieldId: `${name}.displayName`,
    label: "Display name",
    error: get(errors, `${name}.displayName`)
  }, /*#__PURE__*/React.createElement(Input, {
    type: "text",
    value: get(values, `${name}.displayName`),
    onChange: handleDisplayNameChange
  })), /*#__PURE__*/React.createElement(Field, {
    fieldId: claimsMadeOrOccurrence,
    label: ""
  }, /*#__PURE__*/React.createElement(RadioButtonGroup, {
    name: "claimsMadeOrOccurrence",
    value: get(values, claimsMadeOrOccurrence),
    options: claimsMadeOrOccurrenceOptions,
    onChange: value => setFieldValue(claimsMadeOrOccurrence, value)
  })), /*#__PURE__*/React.createElement(Field, {
    fieldId: coverageTerritory,
    label: "Coverage Territory"
  }, /*#__PURE__*/React.createElement(RadioButtonGroup, {
    name: "coverageTerritory",
    value: get(values, coverageTerritory),
    options: coverageTerritoryTypeOptions,
    onChange: value => setFieldValue(coverageTerritory, value)
  })), /*#__PURE__*/React.createElement(Field, {
    fieldId: auditable,
    label: "Auditable?"
  }, /*#__PURE__*/React.createElement(AdvancedSelectInput, {
    value: get(values, auditable),
    options: auditableTypeOptions,
    onChange: value => setFieldValue(auditable, value)
  })), /*#__PURE__*/React.createElement(FormikSchemaInput, {
    name: `${name}.data`,
    schema: data === null || data === void 0 ? void 0 : data.schema
  }), /*#__PURE__*/React.createElement(Field, {
    fieldId: terrorismRiskInsuranceAct,
    label: "Terrorism Risk Insurance Act (TRIA)"
  }, /*#__PURE__*/React.createElement(AdvancedSelectInput, {
    value: get(values, terrorismRiskInsuranceAct),
    options: terrorismRiskInsuranceActTypeOptions,
    onChange: value => setFieldValue(terrorismRiskInsuranceAct, value)
  })), /*#__PURE__*/React.createElement(Field, {
    fieldId: additionalDetails,
    label: "Additional Details"
  }, /*#__PURE__*/React.createElement(TextareaAutosize, {
    "data-testid": additionalDetails,
    value: get(values, additionalDetails),
    onChange: event => setFieldValue(additionalDetails, event.target.value)
  })));
}

function EditCoverageDetailsModal(_ref) {
  let {
    lineOfCoverage,
    onToggle
  } = _ref;
  const {
    version,
    type,
    uuid
  } = lineOfCoverage;
  const targetRef = /*#__PURE__*/React.createRef();
  const notifications = useNotifications();
  const {
    useMutation,
    invalidateQuery
  } = useAdminTRPC();
  const {
    mutateAsync: updateLineOfCoverage
  } = useMutation('loc.updateData', {
    onSuccess: async () => {
      await invalidateQuery(['loc.getByUuid', {
        lineOfCoverageUuid: lineOfCoverage.uuid
      }], {
        exact: false
      });
      onToggle();
      notifications.add({
        type: 'success',
        title: 'Line of coverage succesfuly updated...'
      });
    }
  });
  const {
    data: lineOfCoverageMetadata,
    isLoading
  } = useLineOfCoverageMetadata({
    lineOfCoverageType: type,
    lineOfCoverageVersion: version
  });
  const initialValues = {
    coverage: lineOfCoverage
  };
  const {
    title
  } = lineOfCoverage;
  return /*#__PURE__*/React.createElement(Modal, {
    hideOnClickOutside: true,
    innerRef: targetRef,
    overlayBgColor: "black",
    overlayOpacity: 0.3,
    isOpen: true,
    onToggle: onToggle
  }, /*#__PURE__*/React.createElement(ModalBody, null, isLoading && !lineOfCoverageMetadata ? /*#__PURE__*/React.createElement(CircleSpinnerInline, null) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ModalHeader, {
    onClose: onToggle,
    titleText: `Edit ${title}`
  }), /*#__PURE__*/React.createElement(Padding, {
    size: 32
  }, /*#__PURE__*/React.createElement(Formik, {
    initialValues: initialValues,
    onSubmit: async values => {
      await updateLineOfCoverage({
        lineOfCoverageUuid: uuid,
        form: values.coverage
      });
    }
  }, _ref2 => {
    let {
      handleSubmit,
      isSubmitting
    } = _ref2;
    return /*#__PURE__*/React.createElement("form", {
      onSubmit: handleSubmit
    }, /*#__PURE__*/React.createElement(Suspense, {
      fallback: /*#__PURE__*/React.createElement(CircleSpinner, {
        label: "Loading coverage details..."
      })
    }, /*#__PURE__*/React.createElement(LineOfCoverageDataInput, {
      name: "coverage",
      lineOfCoverageType: type,
      lineOfCoverageVersion: version
    })), /*#__PURE__*/React.createElement(ModalFooter, null, /*#__PURE__*/React.createElement(Stack, {
      gap: 8,
      direction: "horizontal"
    }, /*#__PURE__*/React.createElement(Button, {
      onClick: onToggle,
      size: "secondary"
    }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
      loading: isSubmitting
    }, "Save"))));
  })))));
}

function LineOfCoverageExpandableDisplay(_ref) {
  let {
    lineOfCoverageUuid,
    onDeleteSuccess,
    canManage = false,
    isLineageFeatureFlagOn,
    getLineOfCoverageDetailsLink,
    getPolicyDetailsLink
  } = _ref;
  const notifications = useNotifications();
  const [isDeleting, setIsDeleting] = useToggle(false);
  const [isEditing, setIsEditing] = useToggle(false);
  const {
    useMutation
  } = useAdminTRPC();
  const {
    data: lineOfCoverage,
    error
  } = useEnrichedLocByUuid({
    lineOfCoverageUuid
  }, {
    suspense: true
  });
  const {
    mutateAsync: handleDelete
  } = useMutation('loc.delete', {
    onSuccess: async () => {
      setIsDeleting();
      onDeleteSuccess === null || onDeleteSuccess === void 0 ? void 0 : onDeleteSuccess();
      notifications.add({
        title: 'Line of coverage removed...',
        type: 'success'
      });
    }
  });

  if (error) {
    throw error;
  } // lineOfCoverage should be defined since we're using suspense


  if (!lineOfCoverage) {
    return null;
  }

  const {
    title
  } = lineOfCoverage;
  const summaryData = [{
    key: 'Status',
    value: POLICY_STATUS_LABEL_MAP[lineOfCoverage.policy.status]
  }, {
    key: 'Effective date',
    value: lineOfCoverage !== null && lineOfCoverage !== void 0 && lineOfCoverage.policy.effectiveDate ? formatDate(lineOfCoverage === null || lineOfCoverage === void 0 ? void 0 : lineOfCoverage.policy.effectiveDate, {
      style: DateFormatStyle.STANDARD,
      timezone: 'UTC'
    }) : '-'
  }, {
    key: 'Expiration date',
    value: lineOfCoverage !== null && lineOfCoverage !== void 0 && lineOfCoverage.policy.expirationDate ? formatDate(lineOfCoverage === null || lineOfCoverage === void 0 ? void 0 : lineOfCoverage.policy.expirationDate, {
      style: DateFormatStyle.STANDARD,
      timezone: 'UTC'
    }) : '-'
  }];
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ExpandableRow, null, /*#__PURE__*/React.createElement(ExpandableHead, {
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Flexbox, {
    width: "100%",
    justifyContent: "space-between",
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Text, {
    weight: 600
  }, title), canManage ? /*#__PURE__*/React.createElement(Flexbox, null, /*#__PURE__*/React.createElement(Tooltip, {
    text: "Edit"
  }, /*#__PURE__*/React.createElement(StyledActionButton$1, {
    onClick: setIsEditing
  }, /*#__PURE__*/React.createElement(IconPencil, {
    color: colors.steel[400]
  }))), /*#__PURE__*/React.createElement(Tooltip, {
    text: "Remove"
  }, /*#__PURE__*/React.createElement(StyledActionButton$1, {
    onClick: setIsDeleting
  }, /*#__PURE__*/React.createElement(IconRemove, {
    color: colors.steel[400],
    size: 12
  })))) : null)), /*#__PURE__*/React.createElement(ExpandableBody, null, /*#__PURE__*/React.createElement(StrippedRows, {
    data: summaryData
  }), /*#__PURE__*/React.createElement(Spacing, {
    height: 24
  }), /*#__PURE__*/React.createElement(LineOfCoverageDisplayInner, {
    lineOfCoverage: lineOfCoverage,
    canManage: canManage,
    getLineOfCoverageDetailsLink: getLineOfCoverageDetailsLink,
    getPolicyDetailsLink: getPolicyDetailsLink,
    isLineageFeatureFlagOn: isLineageFeatureFlagOn
  }))), isDeleting && /*#__PURE__*/React.createElement(ActionModal, {
    variant: "dangerous",
    confirmText: "Yes, delete coverage",
    isOpen: true,
    onClose: setIsDeleting,
    onConfirm: () => handleDelete({
      lineOfCoverageUuid: lineOfCoverage.uuid
    }),
    title: "Are you sure you want to delete this line of coverage?",
    cancelText: "Cancel"
  }, "You will not be able to recover these coverage details."), isEditing && /*#__PURE__*/React.createElement(EditCoverageDetailsModal, {
    onToggle: setIsEditing,
    lineOfCoverage: lineOfCoverage
  }));
}
const StyledActionButton$1 = styled.button`
  align-items: center;
  background: #fff;
  border: none;
  border-radius: 50%;
  display: flex;
  height: 32px;
  justify-content: center;
  width: 32px;
  background: none;
  pointer-events: initial;
  transition: background 0.3s ease;
  cursor: pointer;

  &:hover {
    background: ${colors.steel[100]};
  }
`;

function InsurableItemScheduledLimits(_ref) {
  let {
    lineOfCoverage,
    insurableItemUuid,
    canManage
  } = _ref;
  return /*#__PURE__*/React.createElement(Suspense, {
    fallback: /*#__PURE__*/React.createElement(Flexbox, {
      justifyContent: "flex-start",
      alignItems: "center"
    }, /*#__PURE__*/React.createElement(CircleSpinnerInline, null))
  }, /*#__PURE__*/React.createElement(InsurableItemScheduledLimitsInner, {
    lineOfCoverage: lineOfCoverage,
    insurableItemUuid: insurableItemUuid,
    canManage: canManage
  }));
}

function InsurableItemScheduledLimitsInner(_ref2) {
  let {
    lineOfCoverage,
    insurableItemUuid,
    canManage
  } = _ref2;
  const {
    type,
    uuid
  } = lineOfCoverage;
  const {
    useQuery
  } = useAdminTRPC();
  const {
    data
  } = useQuery(['scheduledLimits.getScheduledLimitsDataForII', {
    lineOfCoverageType: type,
    lineOfCoverageUuid: uuid,
    insurableItemUuid
  }], {
    suspense: true
  });

  if (!data) {
    return null;
  }

  return /*#__PURE__*/React.createElement(ScheduledLimitsPermissionsProvider, {
    canManage: canManage
  }, /*#__PURE__*/React.createElement(ScheduledLimitsForIISpreadsheet, {
    insurableItemsScheduledLimits: data.scheduleLimitMetadataResponse.insurableItemsScheduledLimits,
    schema: data.scheduleLimitMetadataResponse.metadata.schema,
    insurableItemsMetadata: data.scheduleLimitMetadataResponse.metadata.insurableItemsMetadata,
    lineOfCoverage: lineOfCoverage,
    insurableItemOption: data.scheduleLimitMetadataResponse.insurableItemOption
  }));
}

function ScheduledItemEditModal(_ref) {
  let {
    lineOfCoverage,
    onToggle,
    scheduledItem
  } = _ref;
  const {
    version,
    type
  } = lineOfCoverage;
  const targetRef = /*#__PURE__*/React.createRef();
  const notifications = useNotifications();
  const {
    useMutation,
    invalidateQuery
  } = useAdminTRPC();
  const {
    mutateAsync: updateScheduledItem
  } = useMutation('scheduledItems.update', {
    onSuccess: async () => {
      await invalidateQuery(['insurableItems.getInsurableItemWithCoverageData', {
        insurableItemUuid: scheduledItem.insurableItemUuid
      }], {
        exact: false
      });
      onToggle();
      notifications.add({
        type: 'success',
        title: 'Scheduled item succesfuly updated...'
      });
    }
  });
  const {
    data: lineOfCoverageMetadata,
    isLoading
  } = useLineOfCoverageMetadata({
    lineOfCoverageType: type,
    lineOfCoverageVersion: version
  });
  const initialValues = {
    scheduledItem
  };
  const {
    title
  } = lineOfCoverage;
  return /*#__PURE__*/React.createElement(Modal, {
    hideOnClickOutside: true,
    innerRef: targetRef,
    overlayBgColor: "black",
    overlayOpacity: 0.3,
    isOpen: true,
    onToggle: onToggle
  }, /*#__PURE__*/React.createElement(ModalBody, null, isLoading && !lineOfCoverageMetadata ? /*#__PURE__*/React.createElement(CircleSpinnerInline, null) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ModalHeader, {
    onClose: onToggle,
    titleText: `Edit Scheduled item on ${title}`
  }), /*#__PURE__*/React.createElement(Formik, {
    initialValues: initialValues,
    onSubmit: async values => {
      await updateScheduledItem({
        scheduledItem: {
          uuid: values.scheduledItem.uuid,
          startDate: values.scheduledItem.startDate,
          endDate: values.scheduledItem.endDate
        }
      });
    }
  }, _ref2 => {
    var _scheduledItem$lineOf;

    let {
      handleSubmit,
      isSubmitting,
      setFieldValue,
      values
    } = _ref2;
    return /*#__PURE__*/React.createElement("form", {
      onSubmit: handleSubmit
    }, /*#__PURE__*/React.createElement(Padding, {
      size: 24
    }, /*#__PURE__*/React.createElement(Stack, {
      gap: 32,
      direction: "vertical"
    }, /*#__PURE__*/React.createElement(Columns, {
      gap: 24
    }, /*#__PURE__*/React.createElement(Field, {
      fieldId: "start-date",
      label: "Coverage start date*"
    }, /*#__PURE__*/React.createElement(DatePickerInput2.Controlled, {
      value: String(values.scheduledItem.startDate),
      onChange: value => setFieldValue('scheduledItem.startDate', value),
      minDate: String(scheduledItem.lineOfCoverage.startDate),
      maxDate: String(scheduledItem.lineOfCoverage.endDate),
      timezone: "UTC",
      format: "MM/DD/YYYY"
    })), /*#__PURE__*/React.createElement(Field, {
      fieldId: "end-date",
      label: "Coverage end date*"
    }, /*#__PURE__*/React.createElement(DatePickerInput2.Controlled, {
      value: String(values.scheduledItem.endDate),
      onChange: value => setFieldValue('scheduledItem.endDate', value),
      minDate: String(scheduledItem.startDate),
      maxDate: String((_scheduledItem$lineOf = scheduledItem.lineOfCoverage) === null || _scheduledItem$lineOf === void 0 ? void 0 : _scheduledItem$lineOf.endDate),
      timezone: "UTC",
      format: "MM/DD/YYYY"
    }))))), /*#__PURE__*/React.createElement(ModalFooter, null, /*#__PURE__*/React.createElement(Stack, {
      gap: 8,
      direction: "horizontal"
    }, /*#__PURE__*/React.createElement(Button, {
      onClick: onToggle,
      size: "secondary",
      loading: isSubmitting
    }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
      loading: isSubmitting
    }, "Save"))));
  }))));
}

function InsurableItemCoverageDisplay(_ref) {
  let {
    canManage,
    scheduledItem,
    insurableItemIdentifier,
    onDeleteSuccess
  } = _ref;
  const {
    title
  } = scheduledItem.lineOfCoverage;
  const {
    useMutation,
    invalidateQuery
  } = useAdminTRPC();
  const notifications = useNotifications();
  const [isDeleting, setIsDeleting] = useToggle(false);
  const [isEditing, setIsEditing] = useToggle(false);
  const summaryData = [{
    key: 'Status',
    value: POLICY_STATUS_LABEL_MAP[scheduledItem.lineOfCoverage.policy.status]
  }, {
    key: 'Coverage effective dates',
    value: `${scheduledItem.lineOfCoverage.policy.effectiveDate ? formatDate(scheduledItem.lineOfCoverage.policy.effectiveDate, {
      style: DateFormatStyle.STANDARD,
      timezone: 'UTC'
    }) : '-'} - ${scheduledItem.lineOfCoverage.policy.expirationDate ? formatDate(scheduledItem.lineOfCoverage.policy.expirationDate, {
      style: DateFormatStyle.STANDARD,
      timezone: 'UTC'
    }) : '-'}`
  }, {
    key: 'Item on the coverage effective dates ',
    value: `${scheduledItem.startDate ? formatDate(scheduledItem.startDate, {
      style: DateFormatStyle.STANDARD,
      timezone: 'UTC'
    }) : '-'} - ${scheduledItem.endDate ? formatDate(scheduledItem.endDate, {
      style: DateFormatStyle.STANDARD,
      timezone: 'UTC'
    }) : '-'}`
  }];
  const {
    mutateAsync: handleDelete
  } = useMutation('scheduledItems.delete', {
    onSuccess: async () => {
      await invalidateQuery(['insurableItems.getInsurableItemWithCoverageData', {
        insurableItemUuid: scheduledItem.insurableItemUuid
      }], {
        exact: false
      });
      onDeleteSuccess === null || onDeleteSuccess === void 0 ? void 0 : onDeleteSuccess();
      notifications.add({
        title: 'Scheduled item removed...',
        type: 'success'
      });
    }
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ExpandableRow, null, /*#__PURE__*/React.createElement(ExpandableHead, {
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Flexbox, {
    width: "100%",
    justifyContent: "space-between",
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Text, {
    weight: 600
  }, title), canManage ? /*#__PURE__*/React.createElement(Flexbox, null, /*#__PURE__*/React.createElement(Tooltip, {
    text: "Edit"
  }, /*#__PURE__*/React.createElement(StyledActionButton, {
    onClick: setIsEditing
  }, /*#__PURE__*/React.createElement(IconPencil, {
    color: colors.steel[400]
  }))), /*#__PURE__*/React.createElement(Tooltip, {
    text: "Remove"
  }, /*#__PURE__*/React.createElement(StyledActionButton, {
    onClick: setIsDeleting
  }, /*#__PURE__*/React.createElement(IconRemove, {
    color: colors.steel[400],
    size: 12
  })))) : null)), /*#__PURE__*/React.createElement(ExpandableBody, null, /*#__PURE__*/React.createElement(StrippedRows, {
    data: summaryData
  }), scheduledItem.lineOfCoverage.isScheduledLimitSupported ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Spacing, {
    height: 24
  }), /*#__PURE__*/React.createElement(InsurableItemScheduledLimits, {
    lineOfCoverage: scheduledItem.lineOfCoverage,
    canManage: canManage,
    insurableItemUuid: scheduledItem.insurableItemUuid
  })) : null, /*#__PURE__*/React.createElement(Spacing, {
    height: 24
  }), /*#__PURE__*/React.createElement(ExpandableModule, {
    label: "Other coverage on the LOC",
    defaultExpanded: true
  }, /*#__PURE__*/React.createElement(LineOfCoverageDataDisplay, {
    lineOfCoverage: scheduledItem.enrichedLineOfCoverage
  })))), isDeleting && /*#__PURE__*/React.createElement(ActionModal, {
    variant: "dangerous",
    confirmText: "Remove",
    isOpen: true,
    onClose: setIsDeleting,
    onConfirm: () => handleDelete({
      scheduledItemUuid: scheduledItem.uuid
    }),
    title: "Remove scheduled item from this LOC",
    cancelText: "Cancel"
  }, "Are you sure you want remove", ' ', /*#__PURE__*/React.createElement(Text, {
    weight: 600,
    display: "inline"
  }, insurableItemIdentifier), ' ', "from", ' ', /*#__PURE__*/React.createElement(Text, {
    weight: 600,
    display: "inline"
  }, title), "?. All of the limits on this coverage will be removed as well."), isEditing && /*#__PURE__*/React.createElement(ScheduledItemEditModal, {
    onToggle: setIsEditing,
    lineOfCoverage: scheduledItem.enrichedLineOfCoverage,
    scheduledItem: scheduledItem
  }));
}
const StyledActionButton = styled.button`
  align-items: center;
  background: #fff;
  border: none;
  border-radius: 50%;
  display: flex;
  height: 32px;
  justify-content: center;
  width: 32px;
  background: none;
  pointer-events: initial;
  transition: background 0.3s ease;
  cursor: pointer;

  &:hover {
    background: ${colors.steel[100]};
  }
`;

function usePolicy(params, options) {
  const {
    useQuery
  } = useAdminTRPC();
  return useQuery(['policies.getByUuid', params], options);
}

export { InsurableItemCoverageDisplay as I, LineOfCoverageCard as L, PolicyCard as P, useEnrichedLocByUuid as a, LineOfCoverageDisplayInner as b, LineOfCoverageExpandableDisplay as c, LineOfCoverageDataDisplay as d, LineOfCoverageDataInput as e, usePolicy as u };
